//purchase
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from '../../Component/Layout';
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import CharacteristicsModal from "../../Component/CharacteristicsModal ";
import { useFieldPermissions } from "../../utils/useFieldPermissions"; // ⚠️ ADJUST — match your actual hooks path
import { authHeaders } from "../../utils/authHeaders";
import MaskedValue from "../../Component/MaskedValue"; // ⚠️ ADJUST — match your actual MaskedValue path

// ⚠️ ADJUST — must match wherever importPurchaseOrder.routes.js is mounted
// in app.js, e.g. app.use("/import-po", require("./.../importPurchaseOrder.routes"))
const IMPORT_PO_API = `${PURCHASE_API}/import-po`;

// react-select compact style used throughout
const rsCompact = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "13px",
    borderColor: "#ced4da",
    boxShadow: "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "13px", position: "absolute" }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

// Rendered outside the modal's scroll container and flips above the field
// automatically when there isn't room below (e.g. near the bottom of a modal).
const rsMenuProps = {
  menuPlacement: "auto",
  menuPosition: "fixed",
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
};

const rsFilter = {
  ...rsCompact,
  control: (base) => ({
    ...rsCompact.control(base),
    height: "36px",
    minHeight: "36px",
  }),
};

// Wraps a field so hovering ANYWHERE over it (not just directly over the
// truncated text) shows the full value in a styled tooltip below it.
// Pass tooltip="" / undefined to render the field with no tooltip behavior.
const FieldWithTooltip = ({ tooltip, children }) => (
  <div
    className="po-field-tooltip"
    data-tooltip={tooltip || ""}
    style={{ position: "relative" }}
  >
    {children}
  </div>
);


// ─────────────────────────────────────────────────────────────
// DateInputDDMMYYYY — always displays/picks dates as dd/mm/yyyy,
// regardless of the browser/OS locale (native <input type="date">
// cannot be forced to a fixed format, which is why it looked
// different on different laptops). Stores/returns "YYYY-MM-DD",
// same as native type="date", so it's a drop-in replacement.
// ─────────────────────────────────────────────────────────────
const ddmmyyyy_pad2 = (n) => String(n).padStart(2, "0");

const ddmmyyyy_isoToDisplay = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
};

const ddmmyyyy_isoToParts = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) };
};

const DDMMYYYY_DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DDMMYYYY_MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DateInputDDMMYYYY = ({
  value,
  onChange,
  disabled = false,
  className = "form-control",
  style = {},
  placeholder = "dd/mm/yyyy",
  min, // optional "YYYY-MM-DD"
  max, // optional "YYYY-MM-DD"
}) => {
  const [open, setOpen] = useState(false);
  const parts = ddmmyyyy_isoToParts(value);
  const today = new Date();
  const [viewYear, setViewYear] = useState(parts ? parts.year : today.getFullYear());
  const [viewMonth, setViewMonth] = useState(parts ? parts.month : today.getMonth());
  const wrapRef = useRef(null);
  const popupRef = useRef(null);
  const CALENDAR_WIDTH = 220;
  const CALENDAR_HEIGHT = 250; // approx rendered height, used to decide flip-above
  const [coords, setCoords] = useState(null); // { top, left, width, placement }

  useEffect(() => {
    const p = ddmmyyyy_isoToParts(value);
    if (p) {
      setViewYear(p.year);
      setViewMonth(p.month);
    }
  }, [value]);

  // Compute a fixed-position anchor for the portalled calendar, flipping
  // above the field when there isn't enough room below (e.g. near the
  // bottom of a modal), so it's never clipped by the modal's own
  // overflow/scroll container or stuck behind other tab content.
  const reposition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < CALENDAR_HEIGHT && rect.top > CALENDAR_HEIGHT;
    setCoords({
      top: placeAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      placement: placeAbove ? "above" : "below",
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScrollOrResize = () => reposition();
    // capture:true so this also fires for scroll on the modal's inner
    // scrollable container, not just the window
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        popupRef.current && !popupRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const selectedParts = ddmmyyyy_isoToParts(value);

  const pickDay = (day) => {
    const iso = `${viewYear}-${ddmmyyyy_pad2(viewMonth + 1)}-${ddmmyyyy_pad2(day)}`;
    if (min && iso < min) return;
    if (max && iso > max) return;
    onChange && onChange(iso);
    setOpen(false);
  };

  const goPrevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  };

  const goNextMonth = () => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  };

  const clearDate = (e) => {
    e.stopPropagation();
    onChange && onChange("");
    setOpen(false);
  };

  // Build calendar grid cells
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const startWeekday = firstOfMonth.getDay(); // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const calendarPopup = open && !disabled && coords && createPortal(
    <div
      ref={popupRef}
      style={{
        position: "fixed",
        top: coords.placement === "above" ? undefined : coords.top,
        bottom: coords.placement === "above" ? (window.innerHeight - coords.top) : undefined,
        left: coords.left,
        zIndex: 100000,
        background: "#fff",
        border: "1px solid #ced4da",
        borderRadius: 6,
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        padding: 10,
        width: CALENDAR_WIDTH,
        fontSize: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button
          type="button"
          onClick={goPrevMonth}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 14, padding: "2px 6px" }}
        >
          ‹
        </button>
        <strong style={{ fontSize: 12 }}>
          {DDMMYYYY_MONTH_NAMES[viewMonth]} {viewYear}
        </strong>
        <button
          type="button"
          onClick={goNextMonth}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 14, padding: "2px 6px" }}
        >
          ›
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center", marginBottom: 4 }}>
        {DDMMYYYY_DAY_NAMES.map((dn) => (
          <div key={dn} style={{ color: "#64748b", fontWeight: 600, padding: "2px 0" }}>
            {dn}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center", rowGap: 2 }}>
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx} />;
          const iso = `${viewYear}-${ddmmyyyy_pad2(viewMonth + 1)}-${ddmmyyyy_pad2(day)}`;
          const isSelected =
            selectedParts &&
            selectedParts.year === viewYear &&
            selectedParts.month === viewMonth &&
            selectedParts.day === day;
          const isDisabledDay = (min && iso < min) || (max && iso > max);
          return (
            <div
              key={idx}
              onClick={() => !isDisabledDay && pickDay(day)}
              style={{
                padding: "4px 0",
                borderRadius: 4,
                cursor: isDisabledDay ? "not-allowed" : "pointer",
                color: isDisabledDay ? "#cbd5e1" : isSelected ? "#fff" : "#1e293b",
                background: isSelected ? "#0d6efd" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (!isSelected && !isDisabledDay) e.currentTarget.style.background = "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.background = "transparent";
              }}
            >
              {day}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <button
          type="button"
          onClick={() => {
            const now = new Date();
            const iso = `${now.getFullYear()}-${ddmmyyyy_pad2(now.getMonth() + 1)}-${ddmmyyyy_pad2(now.getDate())}`;
            if ((min && iso < min) || (max && iso > max)) return;
            onChange && onChange(iso);
            setOpen(false);
          }}
          style={{
            border: "1px solid #ced4da",
            background: "#fff",
            borderRadius: 4,
            fontSize: 11,
            padding: "3px 8px",
            cursor: "pointer",
          }}
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            border: "1px solid #ced4da",
            background: "#fff",
            borderRadius: 4,
            fontSize: 11,
            padding: "3px 8px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>,
    document.body
  );

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block", width: "100%" }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          readOnly
          disabled={disabled}
          className={className}
          style={{ ...style, cursor: disabled ? "not-allowed" : "pointer", background: disabled ? "#e9ecef" : (style.background || "#fff") }}
          value={ddmmyyyy_isoToDisplay(value)}
          placeholder={placeholder}
          onClick={() => !disabled && setOpen((o) => !o)}
        />
        {!disabled && value && (
          <span
            onClick={clearDate}
            title="Clear date"
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: "#94a3b8",
              fontSize: 13,
              lineHeight: 1,
            }}
          >
            ✕
          </span>
        )}
      </div>

      {calendarPopup}
    </div>
  );
};

function getCurrentUser() {
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const parsedUser = JSON.parse(userObj);
      const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
      if (fullName) return fullName;
      if (parsedUser.email) return parsedUser.email;
    } catch (e) {
      console.error("Error parsing user data:", e);
    }
  }
  const storedName = localStorage.getItem("userName");
  if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
  return "";
}


// 🆕 IMPORT PO — LANDED COST — 5 top-level tabs (Material / Logistics /
// Customs / Port-Clearing / Bank), same row as Basic/Product/Confirmation.
// `key` maps 1:1 to the `landed_${key}` column added on purchase_orders
// (see ensurePoLandedCostColumns in purchaseOrder.service.js) and to the
// same-named field sent in productDetails on save.
const LANDED_COST_GROUPS = [
  {
    group: "Customs",
    tabId: "landed_customs",
    perLineItem: false, // 🆕 single entry for the whole PO — not repeated per product line item, and no Product Code column
    fields: [],
  },
  {
    group: "Port/Clearing",
    tabId: "landed_port_clearing",
    perLineItem: false, // 🆕 single entry for the whole PO — not repeated per product line item, and no Product Code column
    fields: [
      { key: "cha_cf_charges", label: "CHA/C&F Charges" },
      { key: "port_charges", label: "Port Charges" },
      { key: "documentation_charges", label: "Documentation Charges" },
      { key: "demurrage", label: "Demurrage" },
      { key: "detention", label: "Detention" },
    ],
  },
  {
    group: "Bank",
    tabId: "landed_bank",
    perLineItem: false, // 🆕 single entry for the whole PO — not repeated per product line item, and no Product Code column
    fields: [
      { key: "lc_charges", label: "LC Charges" },
      { key: "bank_charges", label: "Bank Charges" },
      { key: "forex_charges", label: "Forex Charges" },
    ],
  },
];

const LANDED_COST_TAB_IDS = LANDED_COST_GROUPS.map(g => g.tabId);

const Custom = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ── Role-based field/module permissions for this module ──────────────
  // DISPLAY-role users get per-field masking (isFieldVisible); everyone
  // else (admin, no-role-assigned, CREATE/CHANGE/REVIEW/CONVERT) sees real
  // values per useFieldPermissions' own rules. hasModuleAccess / permission
  // helpers below gate which actions/tabs render at all.
  const {
    loading: permLoading,
    isAdmin,
    hasRole,
    permissionType: poPermissionType,
    hasModuleAccess,
    isReviewOnly,
    isConvertOnly,
    isFieldVisible
  } = useFieldPermissions("IMPORT_PURCHASE_ORDER");

  // Convenience flags mirroring the CREATE/CHANGE meaning documented in
  // authController.js / Role.js for this module's permission types.
  // Import POs are generated by the import flow, not created ad-hoc from
  // this screen — same as the original Custom.js (view/edit only).
  const canCreatePO = false;
  const canEditPO = isAdmin || !hasRole || poPermissionType === "CHANGE";

  const [poList, setPoList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [createActiveTab, setCreateActiveTab] = useState("basic");
  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [calculatedAmounts, setCalculatedAmounts] = useState({});
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState('');
  const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [paymentTerms, setPaymentTerms] = useState('');
  const [modeOfTransport, setModeOfTransport] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [freightTerms, setFreightTerms] = useState("");
  const [insuranceTerms, setInsuranceTerms] = useState("");
  const [transporter, setTransporter] = useState("");
  const [poHeaderLotType, setPoHeaderLotType] = useState("");
  const [productDetails, setProductDetails] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [discounts, setDiscounts] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [comment, setComment] = useState("");
  const [actionType, setActionType] = useState("");
  const [commentModal, setCommentModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [confirmationData, setConfirmationData] = useState(modalData);
  const [editableProducts, setEditableProducts] = useState([]);
  const [selectedPurchaseOrganization, setSelectedPurchaseOrganization] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [uploadedDocs, setUploadedDocs] = useState([]);
  // PO document upload size limit — configurable via
  // Configuration → File Size Config (Module = "Purchase Order").
  // Falls back to 50 KB (the old implicit/no-limit default) if it can't be fetched.
  const DEFAULT_MAX_PO_DOC_SIZE_KB = 50;
  const [maxPoDocSizeKb, setMaxPoDocSizeKb] = useState(DEFAULT_MAX_PO_DOC_SIZE_KB);
  const [filterVendor, setFilterVendor] = useState("");
  const [isPoSaved, setIsPoSaved] = useState(false);
  // 🆕 Whether this Import PO already has a row saved in
  // custom_purchase_orders (backend returns has_actuals per line — see
  // Custom.service.js getCustomPurchaseOrderById). Drives the
  // "first save is a single click" footer button below.
  const [hasCustomData, setHasCustomData] = useState(false);
  const [poLocation, setPoLocation] = useState("")
  const [locationOptions, setLocationOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  // ── Product filter state ──────────────────────────────
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubCategory, setFilterSubCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [poCreatedDate, setPoCreatedDate] = useState("");
const [poApprovedDate, setPoApprovedDate] = useState("");

  const [purchaseOrganizations,
    setPurchaseOrganizations] =
    useState([]);
  //eslint-disable-next-line no-unused-vars
  const [createPurchasePlanner, setCreatePurchasePlanner] = useState("");
  //eslint-disable-next-line no-unused-vars
  const [createPurchaseOrganization, setCreatePurchaseOrganization] = useState("");

  const [purchasePlanners,
    setPurchasePlanners] =
    useState([]);

  const [modeOfTransportOptions, setModeOfTransportOptions] = useState([]);
  const [deliveryTypeOptions, setDeliveryTypeOptions] = useState([]);
  const [freightTermsOptions, setFreightTermsOptions] = useState([]);
  const [insuranceTermsOptions, setInsuranceTermsOptions] = useState([]);
  const [transporterOptions, setTransporterOptions] = useState([]);
  const [poHeaderLotTypeOptions, setPoHeaderLotTypeOptions] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [poProductType, setPoProductType] = useState("");

  // 🆕 ORDER TYPE — Domestic PO / Import PO. When Import PO is selected,
  // an extra "Import Details" header section (and extra item columns on
  // the Product tab) become visible.
  const [poOrderType, setPoOrderType] = useState("domestic");
  const orderTypeOptions = [
    { value: "domestic", label: "Domestic PO" },
    { value: "import", label: "Import PO" },
  ];

  // 🆕 IMPORT PO — header-level fields (only relevant when poOrderType === "import")
  const [importSupplierCountry, setImportSupplierCountry] = useState("");

  // 🆕 Dynamic tax column headings for the Product tab's 3 tax columns.
  // Domestic POs always show CGST/SGST/IGST (the historical behavior).
  // Import POs replace these with whatever tax_type(s) were actually
  // configured on the GST Configuration screen for this HSN + Supplier
  // Country (e.g. just "IGST", or a custom type like "BCD"), so the
  // heading the user sees always matches what's really being charged
  // instead of an assumed CGST/SGST/IGST split. Reset back to the
  // defaults whenever GST can't resolve (no country/HSN match yet).
  const DEFAULT_GST_COLUMN_LABELS = { col1: "CGST", col2: "SGST", col3: "IGST" };
  const [gstColumnLabels, setGstColumnLabels] = useState(DEFAULT_GST_COLUMN_LABELS);

  // Given the raw `taxes: [{ type, percent }]` array gst_configuration
  // returns for a product, produce { col1, col2, col3 } labels — used only
  // for Import POs. Falls back to defaults when there's nothing to show.
  const deriveGstColumnLabels = (taxesByProduct) => {
    for (const code of Object.keys(taxesByProduct || {})) {
      const taxes = taxesByProduct[code]?.taxes;
      if (taxes && taxes.length > 0) {
        return {
          col1: taxes[0]?.type || DEFAULT_GST_COLUMN_LABELS.col1,
          col2: taxes[1]?.type || "",
          col3: taxes[2]?.type || "",
        };
      }
    }
    return DEFAULT_GST_COLUMN_LABELS;
  };

  const [importCurrency, setImportCurrency] = useState("");
  const [importExchangeRate, setImportExchangeRate] = useState("");
  const [importIncoterm, setImportIncoterm] = useState("");
  const [importPortOfLoading, setImportPortOfLoading] = useState("");
  const [importPortOfDischarge, setImportPortOfDischarge] = useState("");
  const [importFinalDestination, setImportFinalDestination] = useState("");
  const [importExpectedShipmentDate, setImportExpectedShipmentDate] = useState("");
  const [importExpectedArrivalDate, setImportExpectedArrivalDate] = useState("");
  const [importLcRequired, setImportLcRequired] = useState("No");
  const [importInsuranceRequired, setImportInsuranceRequired] = useState("No");

  //eslint-disable-next-line
  const [poCategory, setPoCategory] = useState("");
  //eslint-disable-next-line
  const [poSubCategory, setPoSubCategory] = useState("");
  const [poCreatedBy, setPoCreatedBy] = useState(getCurrentUser());
  const [poApprovedBy, setPoApprovedBy] = useState("");

  const [changeLogs, setChangeLogs] = useState([]);
  const [advancePayments, setAdvancePayments] = useState([]);
  const [paymentHistoryTabType, setPaymentHistoryTabType] = useState("Advance");
  const [paymentHistoryTabAmount, setPaymentHistoryTabAmount] = useState("");
  const [paymentHistoryTabDescription, setPaymentHistoryTabDescription] = useState("");
  const [paymentHistoryTabReference, setPaymentHistoryTabReference] = useState("");
  const [paymentHistoryTabDate, setPaymentHistoryTabDate] = useState(
    (() => {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    })()
  );
  const [editingPaymentId, setEditingPaymentId] = useState(null); // 🆕 id of the advance-payment row currently being edited (null = adding a new payment)

  // 🆕 ACKNOWLEDGEMENT tab
  const [acknowledgements, setAcknowledgements] = useState([]);
  const [ackNoInput, setAckNoInput] = useState("");
  const [ackDateInput, setAckDateInput] = useState(
    (() => {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    })()
  );
  const [editingAckId, setEditingAckId] = useState(null); // id of the acknowledgement row currently being edited (null = adding new)
  const [paymentTermNumber, setPaymentTermNumber] = useState("");
  //eslint-disable-next-line no-unused-vars
  const [paymentTermUnit, setPaymentTermUnit] = useState("days");
  const [quoteNo, setQuoteNo] = useState("");
  const [quoteDate, setQuoteDate] = useState("");
  const [isEditMode, setIsEditMode] = useState(false); // false = view mode, true = edit mode
  const [isSubmittingPO, setIsSubmittingPO] = useState(false); // guards against double-click save+submit
  const [listTab, setListTab] = useState("active"); // "active" | "short_closed"
  const [deletedLineItemIds, setDeletedLineItemIds] = useState([]); // track deleted row DB ids for save
  const [showPODocInModal, setShowPODocInModal] = useState(false);

  // ── Characteristics modal state ──────────────────────────────
  const [modalVisible, setModalVisible] = useState(false);
  const [charModalRowIdx, setCharModalRowIdx] = useState(null);
  //eslint-disable-next-line
  const [charModalSource, setCharModalSource] = useState(null); // "product" | "purchase"
  const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);
  // class_code -> Set of characteristic names configured under that class
  // (Product Class vs Purchase Class each have their own characteristic
  // list; this lets the ▶ icons show only the characteristics that
  // actually belong to the class they're next to, instead of the full
  // merged list coming back from the PIR / PO characteristics endpoint).
  const [classCharacteristicMap, setClassCharacteristicMap] = useState({});

  useEffect(() => {
    axios
      .get(`${MASTER_API}/product-config/getProductConfigs`)
      .then((res) => {
        const map = {};
        (res.data || []).forEach((cfg) => {
          map[cfg.class_code] = new Set(
            (cfg.characteristics || []).map((c) =>
              (c.characteristic || "").trim().toLowerCase()
            )
          );
        });
        setClassCharacteristicMap(map);
      })
      .catch(() => setClassCharacteristicMap({}));
  }, []);



  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // If already in YYYY-MM-DD format, return as-is to avoid UTC timezone shift
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // For ISO strings like "2024-05-15T00:00:00.000Z", extract the date part directly
    // without constructing a Date object (which would shift due to UTC vs local timezone)
    if (typeof dateStr === "string" && dateStr.includes("T")) {
      return dateStr.split("T")[0];
    }
    // Fallback: use local date methods (not UTC) to avoid -1 day in IST (UTC+5:30)
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Converts a YYYY-MM-DD string to DD/MM/YYYY for display purposes only.
  // Leaves the underlying state (used for saving/API calls) untouched.
  const formatDateDMY = (dateStr) => {
    if (!dateStr) return "";
    const isoDate = formatDate(dateStr); // normalize to YYYY-MM-DD first
    const parts = isoDate.split("-");
    if (parts.length !== 3) return isoDate;
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  };

  // Returns today's date as YYYY-MM-DD in LOCAL timezone (not UTC)
  const getTodayLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [confirmationLines, setConfirmationLines] = useState({});

  // 🆕 IMPORT PO — Landed Cost tab. Keyed by rowKey (row.id for saved
  // lines, `idx_${idx}` for lines not yet saved) -> { [fieldKey]: value }.
  // Same keying pattern as confirmationLines above.
  const [landedCosts, setLandedCosts] = useState({});
  const [confirmQtys, setConfirmQtys] = useState(modalData?.map(row => row.confirm_qty || '') || []);
  const [confirmDates, setConfirmDates] = useState(modalData?.map(row => formatDate(row.confirm_date)) || []);
  const [deliverQtys, setDeliverQtys] = useState(modalData?.map(row => row.deliver_qty || '') || []);
  const [deliverDates, setDeliverDates] = useState(modalData?.map(row => formatDate(row.deliver_date)) || []);
  const [receivingDates, setReceivingDates] = useState(modalData?.map(row => formatDate(row.receiving_date)) || []);
  const [transportationDates, setTransportationDates] = useState(modalData?.map(row => formatDate(row.transportation_date)) || []);
  const [packingDates, setPackingDates] = useState(modalData?.map(row => formatDate(row.packing_date)) || []);
  const [pickingDates, setPickingDates] = useState(modalData?.map(row => formatDate(row.picking_date)) || []);
  const [madDates, setMadDates] = useState(modalData?.map(row => formatDate(row.mad)) || []);




  const fetchLogs = async (poId) => {
    try {
      const res = await axios.get(`${IMPORT_PO_API}/logs/${poId}`, { headers: authHeaders() });
      if (res.data.success) {
        setChangeLogs(res.data.logs);
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
    }
  };

 const fetchAdvancePayments = async (poId) => {
  try {
    const res = await axios.get(`${IMPORT_PO_API}/advance-payments/${poId}`, { headers: authHeaders() });

    console.log(res.data.advancePayments);

    if (res.data.success) {
      setAdvancePayments(res.data.advancePayments);
    }
  } catch (err) {
    console.error("Error fetching advance payments:", err);
  }
};

// 🆕 Load saved acknowledgement rows for this PO, and populate the
// Acknowledgement No / Date box with the most recent saved row so the
// box always reflects the exact saved value instead of sitting empty.
const fetchAcknowledgements = async (poId) => {
  try {
    const res = await axios.get(`${IMPORT_PO_API}/acknowledgements/${poId}`, { headers: authHeaders() });

    if (res.data.success) {
      const rows = res.data.acknowledgements || [];
      setAcknowledgements(rows);

      if (rows.length > 0) {
        // Don't assume API ordering — pick the row with the newest date,
        // falling back to the last row in the array if dates are missing/equal.
        const latest = rows.reduce((best, row) => {
          if (!best) return row;
          const bestTime = new Date(best.acknowledgement_date || 0).getTime();
          const rowTime = new Date(row.acknowledgement_date || 0).getTime();
          return rowTime >= bestTime ? row : best;
        }, null);
        setAckNoInput(latest.acknowledgement_no || "");
        setAckDateInput(
          latest.acknowledgement_date ? String(latest.acknowledgement_date).slice(0, 10) : ackDateInput
        );
        // 🆕 Track which saved row this is so Save updates it (PUT) instead
        // of creating a duplicate (POST) — the row-level edit button is gone
        // now that the history table has been removed.
        setEditingAckId(latest.id);
      } else {
        // 🆕 This PO has no saved acknowledgement rows — clear the box
        // instead of leaving the previous PO's values sitting there.
        setAckNoInput("");
        setAckDateInput("");
        setEditingAckId(null);
      }
    }
  } catch (err) {
    console.error("Error fetching acknowledgements:", err);
  }
};

  const [navigatedFromGR, setNavigatedFromGR] = useState(false);

  // Auto-open modal when navigated from Goods Receipt page with a PO number in state
  useEffect(() => {
    if (location.state?.openPoNo) {
      setNavigatedFromGR(true);
      viewPO(location.state.openPoNo);
      // Clear router state so a page refresh doesn't re-open the modal
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (modalData?.length) {
      setEditableProducts(prev => {
        // Only initialize rows that don't exist yet — never overwrite existing edits
        const next = modalData.map((row, i) => {
          if (prev[i]) return prev[i]; // preserve existing edits
          return {
            product_cost: row.product_cost,
            base_cost_inr: row.base_cost_inr ?? row.product_cost, // 🆕 for exchange-rate re-conversion
            requirement_qty: row.requirement_qty,
            delivery_date: formatDate(row.delivery_date),
          };
        });
        return next;
      });
    }
  }, [modalData]);

  useEffect(() => {
    fetch(`${MASTER_API}/configuration/file-size-limit/${encodeURIComponent("Purchase Order")}`, { headers: authHeaders() })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        if (data && data.maxSizeKb) setMaxPoDocSizeKb(data.maxSizeKb);
      })
      .catch(() => { /* keep default limit if config fetch fails */ });
  }, []);

  // Fetch persisted docs list from the server whenever the Docs tab is
  // opened for a saved PO (has a real purchase_order_id).
  useEffect(() => {
    const poId = modalData?.[0]?.purchase_order_id || "";
    if (createActiveTab === "docs" && poId) {
      fetchUploadedDocs(poId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createActiveTab, modalData]);

  // Splits a FileList/array into files within the limit and files that exceed it,
  // and toasts a warning listing any rejected files. Same pattern as
  // SalesOrder.js's filterFilesWithinSizeLimit — keep both in sync.
  const filterFilesWithinSizeLimit = (files) => {
    const maxBytes = maxPoDocSizeKb * 1024;
    const okFiles = [];
    const rejected = [];
    files.forEach(f => {
      if (f.size > maxBytes) {
        rejected.push(f.name);
      } else {
        okFiles.push(f);
      }
    });
    if (rejected.length) {
      toast.error(
        `${rejected.length} file(s) exceed the ${maxPoDocSizeKb} KB upload limit and were skipped: ${rejected.join(', ')}`
      );
    }
    return okFiles;
  };

  const [docsUploading, setDocsUploading] = useState(false);

  const fetchUploadedDocs = (poId) => {
    if (!poId) { setUploadedDocs([]); return; }
    axios.get(`${IMPORT_PO_API}/list-docs/${encodeURIComponent(poId)}`, { headers: authHeaders() })
      .then(res => setUploadedDocs(res.data?.files || []))
      .catch(() => setUploadedDocs([]));
  };

  const handleDocUpload = async (e) => {
    const rawFiles = Array.from(e.target.files);
    e.target.value = ""; // allow re-selecting the same file name later

    const poId = modalData?.[0]?.purchase_order_id || "";
    if (!poId) {
      toast.error("Save the PO before attaching documents.");
      return;
    }

    const okFiles = filterFilesWithinSizeLimit(rawFiles);
    if (!okFiles.length) return;

    const formData = new FormData();
    okFiles.forEach((f) => formData.append("docs", f));

    setDocsUploading(true);
    try {
      await axios.post(
        `${IMPORT_PO_API}/upload-docs/${encodeURIComponent(poId)}`,
        formData,
        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
      );
      fetchUploadedDocs(poId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to upload document(s)");
    } finally {
      setDocsUploading(false);
    }
  };

  const handleDocDownload = async (doc) => {
    const poId = modalData?.[0]?.purchase_order_id || "";
    if (!poId || !doc.filename) return;
    try {
      const res = await fetch(
        `${IMPORT_PO_API}/download-doc/${encodeURIComponent(poId)}/${encodeURIComponent(doc.filename)}`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.originalname || doc.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Failed to download file");
    }
  };

  useEffect(() => {

    // PURCHASE ORDERS

    axios
      .get(`${IMPORT_PO_API}/import-purchase-orders`, { headers: authHeaders() })
      .then((res) => {

        if (res.data.success) {

          setAllPurchaseOrders(
            res.data.purchaseOrders
          );

          setPoList(
            res.data.purchaseOrders
          );

          setPurchaseOrders(
            res.data.purchaseOrders
          );
        }

      })
      .catch((err) =>
        console.error(
          "Error fetching purchase orders:",
          err
        )
      );

    // VENDORS

    axios
      .get(`${PURCHASE_API}/po/vendors`, { headers: authHeaders() })
      .then((res) => {
        // 🛠️ FIX — ${IMPORT_PO_API}/vendors (import-po/vendors) doesn't
        // reliably return a `name` field on every row, so
        // `vendors.find(v => v.id === selectedVendor)?.name` was coming
        // back undefined and every fallback (`|| selectedVendor`) in the
        // Supplier dropdown/tooltip ended up showing the vendor CODE
        // instead of the vendor NAME. Switched to the same `po/vendors`
        // endpoint the regular Purchase Order screen uses, which does
        // return the name. Keep the normalization below as a safety net
        // in case any row is still missing a name field.
        const rawList = Array.isArray(res.data) ? res.data : [];
        const list = rawList.map((v) => ({
          ...v,
          id: v.id ?? v.vendor_code ?? v.code,
          name:
            v.name ||
            v.vendor_name ||
            v.business_partner_name ||
            v.supplier_name ||
            v.id ||
            v.vendor_code ||
            v.code ||
            "",
        }));
        setVendors(list);
      })
      .catch((err) =>
        console.error(
          "Error fetching vendors:",
          err
        )
      );

    // PURCHASE ORGANIZATION

    axios
      .get(
        `${MASTER_API}/configuration/purchase-orgs`
      )
      .then((res) => {
        // Handle {success, data:[]} or plain array
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        setPurchaseOrganizations(list);
      })
      .catch((err) =>
        console.error(
          "Purchase Org Error:",
          err
        )
      );

    // PURCHASE PLANNER

    axios
      .get(
        `${MASTER_API}/configuration/purchase-planners`
      )
      .then((res) => {
        // Handle {success, data:[]} or plain array
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        setPurchasePlanners(list);
      })
      .catch((err) =>
        console.error(
          "Purchase Planner Error:",
          err
        )
      );

    // LOCATION

    axios
      .get(
        `${MASTER_API}/configuration/location`
      )
      .then((res) => {

        // Handle both plain array and {success, data:[]} shape
        const locRaw = res.data;
        const locList = Array.isArray(locRaw) ? locRaw
          : Array.isArray(locRaw?.data) ? locRaw.data : [];
        setLocationOptions(locList);

      })
      .catch((err) =>
        console.error(
          "Location Error:",
          err
        )
      );

    // COUNTRY — same master used by the GST Configuration screen's
    // Country dropdown, so Supplier Country here can never drift from
    // what gst_configuration rows are actually keyed to.
    axios
      .get(
        `${MASTER_API}/configuration/country`
      )
      .then((res) => {
        const countryRaw = res.data;
        const countryList = Array.isArray(countryRaw) ? countryRaw
          : Array.isArray(countryRaw?.data) ? countryRaw.data : [];
        setCountryOptions(countryList);
      })
      .catch((err) =>
        console.error(
          "Country Error:",
          err
        )
      );




    axios.get(`${MASTER_API}/configuration/dynamic/mode_of_transport`, { headers: authHeaders() })
      .then(res => setModeOfTransportOptions(res.data || []));

    axios.get(`${MASTER_API}/configuration/dynamic/delivery_type`, { headers: authHeaders() })
      .then(res => setDeliveryTypeOptions(res.data || []));

    axios.get(`${MASTER_API}/configuration/dynamic/freight_terms`, { headers: authHeaders() })
      .then(res => setFreightTermsOptions(res.data || []));

    axios.get(`${MASTER_API}/configuration/dynamic/insurance_terms`, { headers: authHeaders() })
      .then(res => setInsuranceTermsOptions(res.data || []));

    axios.get(`${MASTER_API}/configuration/dynamic/transporter`, { headers: authHeaders() })
      .then(res => setTransporterOptions(res.data || []));

    axios.get(`${MASTER_API}/configuration/dynamic/po_header_lot_type`, { headers: authHeaders() })
      .then(res => setPoHeaderLotTypeOptions(res.data || []));


  }, []);



  useEffect(() => {
    axios
      .get(
        `${IMPORT_PO_API}/product-details`,
        { headers: authHeaders() }
      )
      .then((res) => {

        setAllProducts(
          res.data || []
        );

      })
      .catch((err) =>
        console.error(err)
      );

    // Fetch category options from configuration
    axios
      .get(`${MASTER_API}/configuration/category`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setCategoryOptions(data);
      })
      .catch((err) => console.error("Category options error:", err));

    // Fetch sub-category options from configuration
    axios
      .get(`${MASTER_API}/configuration/sub-category`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setSubCategoryOptions(data);
      })
      .catch((err) => console.error("Sub-category options error:", err));

    // Fetch product type options
    axios
      .get(`${MASTER_API}/configuration/product-types`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setProductTypeOptions(data.map(i => ({ value: i.name || i, label: i.name || i })));
      })
      .catch((err) => console.error("Product type options error:", err));

  }, []);



  const fetchPOs = () => {
    const filteredData = allPurchaseOrders.filter((po) => {
      const matchesPurchaseOrderId =
        selectedPurchaseOrderId ? po.purchase_order_id === selectedPurchaseOrderId : true;

      // Fix: compare against po.purchase_organization, not po.purchase_order_id
      const matchesPurchaseOrganization =
        selectedPurchaseOrganization
          ? (po.purchase_organization || "")
            .toLowerCase()
            .includes(selectedPurchaseOrganization.toLowerCase())
          : true;

      const matchesVendor = filterVendor
        ? po.vendor_name === filterVendor
        : true;


      return (
        matchesPurchaseOrderId &&
        matchesPurchaseOrganization &&
        matchesVendor
      );
    });

    setPoList(filteredData);
  };



  useEffect(() => {
    if (showModal && modalData?.length && !selectedVendor) {
      const vendor = vendors.find(
        v => v.name === modalData?.[0].vendor_name
      );
      if (vendor) setSelectedVendor(vendor.id);
    }
  }, [showModal, modalData, vendors, selectedVendor]);

  useEffect(() => {
    fetchPOs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allPurchaseOrders,
    selectedPurchaseOrderId,
    selectedPurchaseOrganization,
    filterVendor
  ]);

  const viewPO = async (poNo) => {
    try {
      const res = await axios.get(`${IMPORT_PO_API}/${poNo}`, { headers: authHeaders() });

      if (res.data && res.data.length > 0) {
        let poData = res.data;

        try {
          const pCodes = poData.map(item => item.product_code);
          const checkRes = await axios.post(`${IMPORT_PO_API}/check-default-vendor`, { productCodes: pCodes }, { headers: authHeaders() });

          if (checkRes.data && checkRes.data.commonVendorCode) {
            const { commonVendorCode, commonVendorName, updatedCosts } = checkRes.data;

            const bpRes = await axios.get(`${IMPORT_PO_API}/business-partner/${commonVendorCode}`, { headers: authHeaders() });
            const vendorData = bpRes.data;

            poData = poData.map(row => {
              // 🐛 FIX: updatedCosts comes from purchase_info_records.productCost,
              // which is always the raw HOME-currency (INR) master cost. For a
              // Domestic PO that's exactly what product_cost should be, so it's
              // fine to auto-refresh it here. But for a saved Import PO,
              // product_cost was deliberately stored in the FOREIGN currency
              // (e.g. 35.50 Yuan, converted from 500 INR at save time) — blindly
              // overwriting it with the raw INR master cost (500) made the Unit
              // Cost box show 500 instead of the actually-saved 35.50, while
              // every other field on the row still matched the DB. Only apply
              // the refreshed cost for Domestic rows; Import rows keep whatever
              // was actually saved.
              const isImportRow = (row.order_type || "").toLowerCase() === "import";
              const rate = Number(row.exchange_rate) || 0;
              return {
                ...row,
                vendor_name: commonVendorName,
                product_cost: isImportRow
                  ? row.product_cost
                  : (updatedCosts[row.product_code] || row.product_cost),
                // Back-derive the original INR cost from what's actually
                // stored (convertedCost / rate) so later exchange-rate edits
                // in this modal re-convert from the right base instead of
                // treating the foreign-currency figure as if it were INR.
                base_cost_inr: isImportRow && rate
                  ? Number(row.product_cost) / rate
                  : row.product_cost,
                contact_person: vendorData.contact_person_name || null,
                business_type: vendorData.business_type || null,
                phone: vendorData.phone || null,
                gstin: vendorData.gstin || null,
                address: vendorData.registered_address || null,
                payment_terms:
                  row.payment_terms ||
                  vendorData.terms_and_conditions ||
                  vendorData.payment_terms ||
                  null
              };
            });
          }
        } catch (err) {
          console.error("Auto Vendor check failed:", err);
        }

        setModalData(poData);
        setEditableProducts(poData.map(row => ({
          product_cost: row.product_cost,
          requirement_qty: row.requirement_qty,
          delivery_date: formatDate(row.delivery_date),
        })));

        // 🆕 Dynamic tax columns from what's ACTUALLY stored in
        // purchase_order_tax (poData[*].taxes, added by getPurchaseOrderById).
        // Previously View/Edit only ever read cgst_percent/sgst_percent/
        // igst_percent, which are always 0 for Import POs (their taxes are
        // named things like "Basic Customs Duty", not CGST/SGST/IGST), so
        // the modal showed CGST/SGST/IGST = 0.00 while the DB actually had
        // 3 real tax rows sitting unused. Deriving straight from row.taxes
        // means the grid always matches the DB, for both domestic and
        // import POs, the moment the modal opens — before the async
        // line-item-calc refresh below even runs.
        setGstColumnLabels(
          deriveGstColumnLabels(
            Object.fromEntries(poData.map(row => [row.id, { taxes: row.taxes || [] }]))
          )
        );
        setCalculatedAmounts(
          Object.fromEntries(poData.map((row, idx) => {
            const t = row.taxes || [];
            return [idx, {
              totalCost: row.total_cost,
              netPriceAfterDiscount: row.net_price_after_discount,
              cgst: Number(t[0]?.percent ?? row.cgst_percent ?? 0),
              sgst: Number(t[1]?.percent ?? row.sgst_percent ?? 0),
              igst: Number(t[2]?.percent ?? row.igst_percent ?? 0),
              cgstAmount: Number(t[0]?.value ?? row.cgst_amount ?? 0),
              sgstAmount: Number(t[1]?.value ?? row.sgst_amount ?? 0),
              igstAmount: Number(t[2]?.value ?? row.igst_amount ?? 0),
              totalGST: row.total_gst_amount,
              finalPriceAfterGST: row.final_price_after_gst,
              hsnCode: row.hsn_code,
              taxes: t,
            }];
          }))
        );

        // 🆕 Landed Cost tab — hydrate per-line-item groups (e.g. Customs)
        // from the landed_* columns on each saved line item, and PO-level
        // groups (Port/Clearing, Bank) once from the first row into the
        // "po_level" pseudo-key.
        const loadedLandedCosts = {};
        const perLineItemGroups = LANDED_COST_GROUPS.filter(g => g.perLineItem !== false);
        const poLevelGroups = LANDED_COST_GROUPS.filter(g => g.perLineItem === false);

        poData.forEach((row, idx) => {
          const rowKey = row.id || `idx_${idx}`;
          const entry = {};
          perLineItemGroups.forEach(g => g.fields.forEach(f => {
            const dbVal = row[`landed_${f.key}`];
            entry[f.key] = (dbVal === null || dbVal === undefined) ? "" : dbVal;
          }));
          loadedLandedCosts[rowKey] = entry;
        });

        if (poData.length > 0) {
          const poLevelEntry = {};
          poLevelGroups.forEach(g => g.fields.forEach(f => {
            const dbVal = poData[0][`landed_${f.key}`];
            poLevelEntry[f.key] = (dbVal === null || dbVal === undefined) ? "" : dbVal;
          }));
          loadedLandedCosts["po_level"] = poLevelEntry;
        }
        setLandedCosts(loadedLandedCosts);
        // NOTE: calculatedAmounts was already populated above from
        // poData[*].taxes (the actual stored DB values) — don't reset it
        // to {} here, or the grid would flash to 0.00 before the async
        // line-item-calc refresh below completes.
        setShowModal(true);

        // 🆕 Resolve GST for every saved line item right away, as its own
        // self-contained async block — NOT awaited inline with the rest of
        // viewPO(). Previously this same fetch lived at the very end of
        // this function; if anything earlier in viewPO() threw (a bad
        // field, a flaky request, etc.), the whole try block would abort
        // before ever reaching it, leaving CGST/SGST/IGST stuck at 0 even
        // though every other field loaded fine — matching exactly what
        // "View" (which calls the same endpoint on its own) could still
        // fix manually per row. Running it here, unawaited, means it
        // starts immediately and completes independently of anything else
        // in this function.
        (async () => {
          const newCalculatedAmounts = {};
          await Promise.all(poData.map(async (row, idx) => {
            const lineItemId = row.id;
            const discount = row.discount || 0;
            const currentCost = row.product_cost;

            if (!lineItemId) return;

            try {
              const prodRes = await fetch(
                `${IMPORT_PO_API}/line-item-calc/${lineItemId}?discount=${discount}&cost=${currentCost}`
              , { headers: authHeaders() });

              if (prodRes.ok) {
                const data = await prodRes.json();

                // Same positional taxes[] mapping as everywhere else: for
                // Import rows data.cgst/sgst/igst are the CGST/SGST/IGST-
                // named split (always 0 for Import, since Import taxes
                // aren't named that) — data.taxes[] has the real breakdown.
                const t = data.taxes || [];
                const isImportRow = t.length > 0 && !["CGST", "SGST", "IGST"].includes(t[0]?.type);

                newCalculatedAmounts[idx] = {
                  totalCost: data.totalCost, netPriceAfterDiscount: data.netPriceAfterDiscount,
                  cgst: isImportRow ? Number(t[0]?.percent ?? 0) : data.cgst,
                  sgst: isImportRow ? Number(t[1]?.percent ?? 0) : data.sgst,
                  igst: isImportRow ? Number(t[2]?.percent ?? 0) : data.igst,
                  cgstAmount: isImportRow ? Number((data.netPriceAfterDiscount * (t[0]?.percent ?? 0) / 100).toFixed(2)) : data.cgstAmount,
                  sgstAmount: isImportRow ? Number((data.netPriceAfterDiscount * (t[1]?.percent ?? 0) / 100).toFixed(2)) : data.sgstAmount,
                  igstAmount: isImportRow ? Number((data.netPriceAfterDiscount * (t[2]?.percent ?? 0) / 100).toFixed(2)) : data.igstAmount,
                  totalGST: data.totalGST, finalPriceAfterGST: data.finalPriceAfterGST, hsnCode: data.hsnCode,
                  taxes: t,
                };

                if (isImportRow) {
                  setGstColumnLabels({
                    col1: t[0]?.type || DEFAULT_GST_COLUMN_LABELS.col1,
                    col2: t[1]?.type || "",
                    col3: t[2]?.type || "",
                  });
                }
              } else {
                console.error(`GST calc request failed for line item ${lineItemId}: HTTP ${prodRes.status}`);
              }
            } catch (err) {
              console.error(`Failed to fetch GST details for Item ID ${lineItemId}`, err);
            }
          }));

          setCalculatedAmounts(prev => ({ ...prev, ...newCalculatedAmounts }));
        })();

        const poStatus = (poData[0].status || "").toUpperCase();
        // 🆕 Does ANY line already have actuals saved in
        // custom_purchase_orders? If not, this PO has never been saved on
        // this screen — open it directly editable and skip the Edit step,
        // so the very first save is a single click.
        const hasActuals = poData.some(r => r.has_actuals);
        setHasCustomData(hasActuals);

        // Auto-edit for new converted POs (PENDING), POs still in CREATED
        // status (no separate Edit step before Submit for them), and any
        // Import PO that hasn't had actuals saved here yet.
        setIsEditMode(poStatus === "PENDING" || poStatus === "CREATED" || !hasActuals);
        setDeletedLineItemIds([]); // reset on every open

        const isSaved = !!poData[0].vendor_name;
        setIsPoSaved(isSaved);
        fetchLogs(poData[0].purchase_order_id);
        fetchAdvancePayments(poData[0].purchase_order_id);
        fetchAcknowledgements(poData[0].purchase_order_id);

        const pTerms = poData[0].payment_terms || "";
        setPaymentTermNumber(pTerms);
        setPaymentTerms(pTerms);
        setPaymentTermUnit("");

        // Auto-fetch payment terms from business_partner if not saved on PO
        if (!pTerms) {
          try {
            const vendorCode = poData[0].vendor_code || poData[0].vendor;
            const vendorName = poData[0].vendor_name || "";
            const lookupId = vendorCode || vendors.find(v =>
              v.name?.toLowerCase() === vendorName?.toLowerCase()
            )?.id;
            if (lookupId) {
              const bpRes = await axios.get(`${IMPORT_PO_API}/business-partner/${lookupId}`, { headers: authHeaders() });
              const bpTerms = bpRes.data?.terms_and_conditions || bpRes.data?.payment_terms || "";
              if (bpTerms) { setPaymentTerms(bpTerms); setPaymentTermNumber(bpTerms); }
            }
          } catch (e) { /* ignore */ }
        }
        setPoLocation(poData[0].location || poData[0].country_of_origin || "");
        setModeOfTransport(poData[0].mode_of_transport || "");
        setDeliveryType(poData[0].delivery_type || "");
        setFreightTerms(poData[0].freight_terms || "");
        setInsuranceTerms(poData[0].insurance_terms || "");
        setTransporter(poData[0].transporter || "");
        setPoHeaderLotType(poData[0].po_header_lot_type || "");

        setVendorDetails({
          business_partner_name: poData[0].vendor_name,
          vendor_name: poData[0].vendor_name,
          contact_person_name: poData[0].contact_person,
          business_type: poData[0].business_type,
          phone: poData[0].phone,
          gstin: poData[0].gstin,
          registered_address: poData[0].address,
        });



        setQuoteNo(poData[0].quote_no || "");

        setQuoteDate(
          poData[0].quote_date
            ? formatDate(poData[0].quote_date)
            : ""
        );

        setPoProductType(poData[0].product_type || poData[0].productType || "");

        // 🆕 Order Type + Import PO header fields
        setPoOrderType(poData[0].order_type || poData[0].orderType || "domestic");
        setImportSupplierCountry(poData[0].supplier_country || "");
        setImportCurrency(poData[0].import_currency || "");
        setImportExchangeRate(poData[0].exchange_rate || "");
        setImportIncoterm(poData[0].incoterm || "");
        setImportPortOfLoading(poData[0].port_of_loading || "");
        setImportPortOfDischarge(poData[0].port_of_discharge || "");
        setImportFinalDestination(poData[0].final_destination || "");
        setImportExpectedShipmentDate(
          poData[0].expected_shipment_date ? formatDate(poData[0].expected_shipment_date) : ""
        );
        setImportExpectedArrivalDate(
          poData[0].expected_arrival_date ? formatDate(poData[0].expected_arrival_date) : ""
        );
        setImportLcRequired(poData[0].lc_required || "No");
        setImportInsuranceRequired(poData[0].insurance_required || "No");

        setPoCategory(poData[0].category || poData[0].productCategory || "");
        setPoSubCategory(poData[0].sub_category || poData[0].subCategory || "");
        setPoCreatedBy(poData[0].created_by || getCurrentUser());
setPoCreatedDate(
  poData[0].created_date
    ? formatDate(poData[0].created_date)
    : getTodayLocal()
);
const poStatusForApproval = (poData[0].status || "").toUpperCase();
setPoApprovedBy(
  (poStatusForApproval === "APPROVED")
    ? (poData[0].approved_by || poData[0].approver_id || "")
    : ""
);
setPoApprovedDate(
  (poStatusForApproval === "APPROVED")
    ? (poData[0].approved_date ? formatDate(poData[0].approved_date) : "")
    : ""
);

        // Init confirmation lines from schedule_line JSON
        const initLines = {};
        poData.forEach((row, rowIndex) => {
          const key = row.id || `idx_${rowIndex}`;
          let lines = [];
          try {
            lines = row.schedule_line ? JSON.parse(row.schedule_line) : [];
          } catch (e) { lines = []; }
          if (!lines.length) {
            lines = [{ schedule_line: "", confirm_qty: row.confirm_qty || "", confirm_date: formatDate(row.confirm_date) || "", deliver_qty: row.deliver_qty || "", deliver_date: formatDate(row.deliver_date) || "" }];
          }
          initLines[key] = lines;
        });
        setConfirmationLines(initLines);

        // Set selectedVendor: use vendor_code (from po.vendor column), else match by name
        const vendorIdOnPO = poData[0].vendor_code || poData[0].vendor || "";
        if (vendorIdOnPO) {
          setSelectedVendor(vendorIdOnPO);
        } else {
          const matchedVendor = vendors.find(v =>
            v.name === poData[0].vendor_name ||
            v.name?.toLowerCase() === poData[0].vendor_name?.toLowerCase()
          );
          setSelectedVendor(matchedVendor ? matchedVendor.id : "");
        }

        const initialDiscounts = {};
        poData.forEach((row, idx) => {
          initialDiscounts[idx] = row.discount || 0;
        });
        setDiscounts(initialDiscounts);
      }
    } catch (error) {
      console.error("Error viewing PO:", error);
      toast.error("Failed to load Purchase Order details");
    }
  };


  const closeModal = () => {
    setShowModal(false);
    setModalData(null);
    setCalculatedAmounts({});
    setIsPoSaved(false);
    setHasCustomData(false);
    setSelectedVendor("");
    setVendorDetails(null);
    setPoLocation("");

    setPaymentTermNumber("");
    setModeOfTransport("");
    setDeliveryType("");
    setFreightTerms("");
    setInsuranceTerms("");
    setTransporter("");
    setPoHeaderLotType("");

    setPaymentTermUnit("days");
    setCreateActiveTab("basic");
    setIsEditMode(false);
    setFilterCategory("");
    setFilterSubCategory("");
    setEditableProducts([]);
    setLandedCosts({}); // 🆕 reset Landed Cost tab entries
    setDeletedLineItemIds([]); // reset deleted rows tracker
    // If we arrived here from Goods Receipt, go back
    if (navigatedFromGR) {
      setNavigatedFromGR(false);
      navigate(-1);
    }
  };

  // 🆕 Shared by handleVendorSelect AND the Purchase Organization field's
  // onChange below — either one can complete the (vendor, purchaseOrg)
  // pair needed to resolve GST, so both need to trigger this refetch.
  // Extracted so the two triggers can't drift out of sync with each other.
  const refetchLineGst = async (vendorId, purchaseOrganization) => {
    if (!modalData || modalData.length === 0) return;
    const pCodes = modalData.map(item => item.product_code);

    const isImport = poOrderType === "import";

    // 🆕 Purchase Organization no longer gates GST resolution — the
    // gst_configuration lookup is keyed by movement_type + hsn_code only
    // now. For Domestic POs the vendor (needed to resolve Intra/Inter
    // state) is required; for Import POs the Supplier Country is required
    // instead (vendor is irrelevant to the Import gst_configuration
    // lookup, which matches on country, not state).
    const canResolve = isImport ? !!importSupplierCountry : !!vendorId;

    if (canResolve) {
      try {
        const gstRes = await axios.post(`${IMPORT_PO_API}/get-product-gst`, {
          productCodes: pCodes,
          vendorCode: vendorId,
          purchaseOrganization,
          orderType: poOrderType,
          supplierCountry: isImport ? importSupplierCountry : undefined
        }, { headers: authHeaders() });
        const newGst = gstRes.data; // { [productCode]: { hsnCode, cgst, sgst, igst, taxes } }

        if (isImport) {
          setGstColumnLabels(deriveGstColumnLabels(newGst));
        } else {
          setGstColumnLabels(DEFAULT_GST_COLUMN_LABELS);
        }

        setCalculatedAmounts(prev => {
          const updated = { ...prev };
          modalData.forEach((row, idx) => {
            const g = newGst[row.product_code];
            if (!g) return;

            // 🆕 If gst_configuration has no matching HSN+movement row for
            // this product/vendor combo, get-product-gst legitimately comes
            // back all-zero. Previously that zero got written straight into
            // the table, even though the product's own master
            // cgst_percent/sgst_percent/igst_percent (already sitting on
            // this row, baked in when the product was first picked) has the
            // real rate — the "View" popup uses exactly that fallback for
            // unsaved rows, which is why View showed the correct % while
            // the table stayed at 0. Apply the same fallback here so both
            // stay in sync regardless of what triggered this refetch
            // (vendor re-select, Purchase Organization change, etc).
            const isAllZero =
              (Number(g.cgst) || 0) === 0 &&
              (Number(g.sgst) || 0) === 0 &&
              (Number(g.igst) || 0) === 0;

            const fallback = {
              cgst: Number(row.cgst_percent ?? 0),
              sgst: Number(row.sgst_percent ?? 0),
              igst: Number(row.igst_percent ?? 0),
            };

            // 🆕 For Import, gst_configuration's tax rows may not literally
            // be named CGST/SGST/IGST (that's the whole point — the heading
            // now reflects whatever was actually configured), so instead of
            // matching by name we drop them into the 3 tax slots
            // positionally: taxes[0] -> slot 1, taxes[1] -> slot 2,
            // taxes[2] -> slot 3. The column headers (gstColumnLabels) show
            // the real names; the existing 3-column calculation pipeline
            // underneath is otherwise untouched.
            const resolved = isImport
              ? {
                  cgst: Number(g.taxes?.[0]?.percent ?? 0),
                  sgst: Number(g.taxes?.[1]?.percent ?? 0),
                  igst: Number(g.taxes?.[2]?.percent ?? 0),
                }
              : (isAllZero ? fallback : { cgst: g.cgst, sgst: g.sgst, igst: g.igst });

            updated[idx] = {
              ...updated[idx],
              cgst: resolved.cgst,
              sgst: resolved.sgst,
              igst: resolved.igst,
              hsnCode: g.hsnCode || updated[idx]?.hsnCode || row.hsn_code
            };
          });
          return updated;
        });
      } catch (e) {
        console.error("Error fetching product GST:", e);
      }
    } else {
      // Nothing to resolve GST against yet (no vendor for Domestic, no
      // Supplier Country for Import) -> force cgst/sgst/igst to 0 for
      // EVERY row — unconditionally, even for rows with no prior
      // calculatedAmounts entry — otherwise calculateLineTotals() falls
      // through to stale row.cgst_percent/sgst_percent/igst_percent from
      // whenever the product was first added, and the table keeps showing
      // numbers that no longer mean anything.
      setGstColumnLabels(DEFAULT_GST_COLUMN_LABELS);
      setCalculatedAmounts(prev => {
        const updated = { ...prev };
        modalData.forEach((row, idx) => {
          updated[idx] = { ...updated[idx], cgst: 0, sgst: 0, igst: 0 };
        });
        return updated;
      });
    }
  };

  // 🆕 Import-PO counterpart to refetchLineGst — takes the country directly
  // (rather than reading importSupplierCountry state) since this is called
  // from the Supplier Country input's onBlur, before/without waiting for a
  // state update to land.
  const refetchLineGstForCountry = async (country) => {
    if (!modalData || modalData.length === 0) return;
    if (!country) {
      setGstColumnLabels(DEFAULT_GST_COLUMN_LABELS);
      setCalculatedAmounts(prev => {
        const updated = { ...prev };
        modalData.forEach((row, idx) => {
          updated[idx] = { ...updated[idx], cgst: 0, sgst: 0, igst: 0 };
        });
        return updated;
      });
      return;
    }

    const pCodes = modalData.map(item => item.product_code);
    try {
      const gstRes = await axios.post(`${IMPORT_PO_API}/get-product-gst`, {
        productCodes: pCodes,
        purchaseOrganization: modalData?.[0]?.purchase_organization || "",
        orderType: "import",
        supplierCountry: country
      }, { headers: authHeaders() });
      const newGst = gstRes.data; // { [productCode]: { hsnCode, taxes } }

      setGstColumnLabels(deriveGstColumnLabels(newGst));

      setCalculatedAmounts(prev => {
        const updated = { ...prev };
        modalData.forEach((row, idx) => {
          const g = newGst[row.product_code];
          if (!g) return;
          updated[idx] = {
            ...updated[idx],
            cgst: Number(g.taxes?.[0]?.percent ?? 0),
            sgst: Number(g.taxes?.[1]?.percent ?? 0),
            igst: Number(g.taxes?.[2]?.percent ?? 0),
            hsnCode: g.hsnCode || updated[idx]?.hsnCode || row.hsn_code
          };
        });
        return updated;
      });
    } catch (e) {
      console.error("Error fetching product GST for import supplier country:", e);
    }
  };

  // 🆕 Auto-fill Currency + Exchange Rate on the Customs tab the moment a
  // Supplier Country is picked, using the Exchange Rate Configuration table
  // (Exchangerateconfig.service.js -> exchange_rate_configuration). We look
  // up the row for our home country -> the selected supplier country.
  //
  // We do NOT filter by the current month/year — Exchange Rate
  // Configuration is usually updated periodically (not guaranteed to have
  // a row for the exact month the PO happens to be created in), so we
  // fetch every India -> <country> row and take the latest one
  // (getExchangeRateList already sorts year DESC, month DESC, id DESC, so
  // rows[0] is always the most recent).
  //
  // ⚠️ ASSUMED "from_country" = "India" (our own company's country). There's
  // no company-country setting elsewhere in this file to read from — this
  // mirrors the isIndia check used for Order Type auto-derivation above
  // (search "vendorCountry.toLowerCase()"). Confirm this matches how
  // exchange_rate_configuration rows are actually entered (India -> X),
  // and adjust if your company/base country differs.
  const EXCHANGE_RATE_CONFIG_URL = `${PURCHASE_API}/exchange-rate-config`;
  const HOME_COUNTRY = "India";

  const fetchExchangeRateForCountry = async (supplierCountry) => {
    if (!supplierCountry) {
      // Reverted to no country (or back to Domestic) — clear and revert costs to base (INR).
      setImportCurrency("");
      setImportExchangeRate("");
      applyExchangeRateToLineItems("", "");
      return;
    }

    try {
      const res = await axios.get(EXCHANGE_RATE_CONFIG_URL, {
        params: {
          from_country: HOME_COUNTRY,
          to_country: supplierCountry
          // 🆕 no month/year here on purpose — see note above
        },
        headers: authHeaders()
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      if (rows.length === 0) {
        toast.error(
          `No exchange rate configured for ${HOME_COUNTRY} -> ${supplierCountry}. ` +
          `Please add one in Exchange Rate Configuration, or enter Currency / Rate manually.`
        );
        setImportCurrency("");
        setImportExchangeRate("");
        return;
      }

      const match = rows[0]; // latest available (list is sorted year/month DESC)
      const rate = Number(match.exchange_rate) || 0;
      const currency = match.to_currency || "";

      setImportCurrency(currency);
      setImportExchangeRate(rate || "");

      applyExchangeRateToLineItems(currency, rate);
    } catch (e) {
      console.error("Error fetching exchange rate config:", e);
      toast.error("Failed to fetch exchange rate for the selected Supplier Country.");
    }
  };

  // 🆕 Push the new Currency + converted Unit Cost into every Product-tab
  // line item. Each row keeps its ORIGINAL cost (as fetched from the
  // product master, in INR) in `base_cost_inr` — captured once when the
  // product is added (see the product-select onChange, and the
  // editableProducts-init useEffect near the top of the component, both
  // updated to stash it) — so this can be re-applied any number of times
  // (country changed again, or reverted to Domestic) without compounding
  // conversions on top of a previously-converted value.
  //
  // NOTE: the grid's Currency cell reads from `modalData[idx].currency`,
  // but the editable Unit Cost input reads from
  // `editableProducts[idx].product_cost` (see the useEffect that inits
  // editableProducts from modalData, and handleProductFieldChange) — these
  // are two separate pieces of state that happen to start out in sync, so
  // both must be updated here together or the grid and the save payload
  // will disagree.
  //
  // ✅ CONFIRMED DIRECTION (from Exchange Rate Configuration screen) —
  // exchange_rate is stored as "1 unit of HOME currency (INR) = <rate>
  // units of the foreign currency" (e.g. the India -> China row shows
  // "1 INR = 0.071 Yuan"). So converting an INR cost INTO the foreign
  // currency MULTIPLIES by the rate.
  const applyExchangeRateToLineItems = (currency, rate) => {
    if (!modalData || modalData.length === 0) return;

    setModalData(prev =>
      prev.map(row => {
        const baseCostInr = Number(row.base_cost_inr ?? row.product_cost ?? 0);
        return {
          ...row,
          base_cost_inr: baseCostInr, // preserve for future re-conversion
          currency: currency || "INR"
        };
      })
    );

    setEditableProducts(prev =>
      modalData.map((row, i) => {
        const existing = prev[i] || {};
        // 🆕 editableProducts (existing) reflects the user's latest manual
        // edit — see handleProductFieldChange — so it must win over
        // modalData's base_cost_inr, which is only set once at
        // product-add time and never touched by manual edits.
        const baseCostInr = Number(
          existing.base_cost_inr ?? row.base_cost_inr ?? row.product_cost ?? 0
        );
        const convertedCost = currency && rate ? baseCostInr * rate : baseCostInr;
        return {
          ...existing,
          base_cost_inr: baseCostInr,
          product_cost: convertedCost
        };
      })
    );
  };

  const handleVendorSelect = async (vendorId) => {
    setSelectedVendor(vendorId);

    if (!vendorId) {
      setVendorDetails(null);
      setPaymentTerms("");
      setPaymentTermNumber("");
      setPaymentTermUnit("days");
      // Supplier cleared -> also re-resolve GST (will zero it out, since
      // there's no vendor half of the pair anymore).
      await refetchLineGst("", modalData?.[0]?.purchase_organization || "");
      return;
    }

    try {
      const response = await axios.get(`${IMPORT_PO_API}/business-partner/${vendorId}`, { headers: authHeaders() });
      setVendorDetails(response.data);
      const vTerms = response.data.terms_and_conditions || response.data.payment_terms || "";
      if (vTerms) {
        setPaymentTerms(vTerms);
        setPaymentTermNumber(vTerms);
      }

      // 🆕 Auto-derive Order Type from the supplier's country — India ->
      // Domestic PO, anything else -> Import PO (with Supplier Country
      // pre-filled from the business partner record). User can still
      // override the Order Type / Supplier Country manually afterwards.
      const vendorCountry = (
        response.data.billing_country ||
        response.data.shipping_country ||
        ""
      ).trim();

      if (vendorCountry) {
        const isIndia = vendorCountry.toLowerCase() === "india";
        setPoOrderType(isIndia ? "domestic" : "import");
        setImportSupplierCountry(isIndia ? "" : vendorCountry);
      }

    // ✅ When supplier is changed manually, use cost from productdetails (not PIR)
if (modalData && modalData.length > 0) {
  const pCodes = modalData.map(item => item.product_code);
  try {
    const costRes = await axios.post(`${IMPORT_PO_API}/get-product-costs`, {
      productCodes: pCodes
    }, { headers: authHeaders() });
    const newCosts = costRes.data; // { [productCode]: cost } — always in INR (product master)

    // 🆕 This cost comes straight from the backend, not from anything the
    // user typed, so the onBlur-based conversion never fires for it. If a
    // foreign Currency + Exchange Rate is already active, convert right
    // here — same "1 INR = <rate> foreign, so multiply" rule as
    // everywhere else — and keep base_cost_inr as the untouched INR
    // figure the API returned, so re-picking a country later converts
    // from this, not from an already-converted number.
    const rate = Number(importExchangeRate) || 0;
    const shouldConvert = poOrderType === "import" && importCurrency && rate;

    setEditableProducts(prev => prev.map((item, idx) => {
      const code = modalData[idx].product_code;
      const rawCostInr = newCosts[code] !== undefined ? Number(newCosts[code]) : Number(item.base_cost_inr ?? item.product_cost ?? 0);
      return {
        ...item,
        base_cost_inr: rawCostInr,
        product_cost: shouldConvert ? rawCostInr * rate : rawCostInr
      };
    }));
  } catch (e) {
    console.error("Error fetching product costs:", e);
  }

  // 🆕 Supplier change can flip Intra State <-> Inter State (different
  // supplier state -> different movement type), which changes whether tax
  // is split CGST+SGST or IGST. Without this, calculateLineTotals() keeps
  // showing the PREVIOUS supplier's cgst_percent/sgst_percent/igst_percent
  // (it falls back to row.cgst_percent from modalData, which was only set
  // when the product was first added / last saved) until the PO is saved
  // and reloaded. Refetch here so the table reflects the new supplier
  // immediately, same as the cost refetch above.
  await refetchLineGst(vendorId, modalData[0]?.purchase_organization || "");
}

    } catch (error) {
      console.error("Error fetching vendor details or costs:", error);
    }
  };

const handleViewProductDetails = async (lineItemId, idx) => {
  try {
    const discount = discounts[idx] || 0;
    const currentCost =
      editableProducts[idx]?.product_cost ||
      modalData[idx]?.product_cost ||
      0;

    // ✅ NEW / UNSAVED ROW — no DB id yet, so calculate locally instead of hitting the server
    if (!lineItemId) {
      const row = modalData[idx] || {};

      const requirement_qty = Number(
        editableProducts[idx]?.requirement_qty ??
        row.requirement_qty ??
        0
      );

      const hsnCode = row.hsn_code || "";
      const cgst = Number(row.cgst_percent ?? 0);
      const sgst = Number(row.sgst_percent ?? 0);
      const igst = Number(row.igst_percent ?? 0);

      const product_cost = Number(currentCost || 0);
      const totalCost = requirement_qty * product_cost;
      const netPriceAfterDiscount = totalCost - (totalCost * discount) / 100;

      const cgstAmount = (netPriceAfterDiscount * cgst) / 100;
      const sgstAmount = (netPriceAfterDiscount * sgst) / 100;
      const igstAmount = (netPriceAfterDiscount * igst) / 100;
      const totalGST = cgstAmount + sgstAmount + igstAmount;
      const finalPriceAfterGST = netPriceAfterDiscount + totalGST;

      // 🆕 Dynamic tax breakup — no unsaved-row `taxes[]` from the server
      // yet, so fall back to whatever the Product tab's own column headers
      // (gstColumnLabels) currently say for this PO, same labels the grid
      // itself is showing. Keeps this modal from hardcoding CGST/SGST/IGST
      // for an Import row that's actually charging Basic Customs Duty etc.
      const taxesArr = [
        { type: gstColumnLabels.col1 || "CGST", percent: cgst, value: cgstAmount },
        gstColumnLabels.col2 ? { type: gstColumnLabels.col2, percent: sgst, value: sgstAmount } : null,
        gstColumnLabels.col3 ? { type: gstColumnLabels.col3, percent: igst, value: igstAmount } : null,
      ].filter(Boolean);

      setProductDetails({
        product_code: row.product_code,
        discount, product_cost, requirement_qty, hsnCode, cgst, sgst, igst,
        totalCost, netPriceAfterDiscount, cgstAmount, sgstAmount, igstAmount,
        totalGST, finalPriceAfterGST, taxes: taxesArr,
      });

      setCalculatedAmounts(prev => ({
        ...prev,
        [idx]: {
          totalCost, netPriceAfterDiscount, cgst, sgst, igst,
          cgstAmount, sgstAmount, igstAmount, totalGST, finalPriceAfterGST, hsnCode
        }
      }));

      setShowProductModal(true);
      return;
    }

    // ── EXISTING / SAVED ROW ──
    // Import PO has no server-side "/line-item-calc/:id" route (that only
    // exists for the regular Purchase Order module) — calling it 404s and
    // shows "Failed to fetch product details." The row already carries
    // everything we need (its own `taxes[]`, same values the Product tab's
    // grid renders), so compute this locally instead of hitting the server,
    // the same way the NEW/unsaved-row branch above already does.
    const row = modalData[idx] || {};

    const requirement_qty = Number(
      editableProducts[idx]?.requirement_qty ??
      row.requirement_qty ??
      0
    );

    const hsnCode = row.hsn_code || "";

    // 🛠️ FIX — this used to read cgst/sgst/igst straight off
    // row.cgst_percent/sgst_percent/igst_percent, which is only ever the
    // value that was last SAVED on the row. It never reflected GST that
    // was re-resolved dynamically after the modal opened (vendor change,
    // HSN/country change, or the async line-item-calc refresh that runs
    // right when View/Edit is opened) — that fresher value lives in
    // calculatedAmounts[idx], which is exactly what the Product tab grid
    // itself reads via calculateLineTotals(). So the per-row "View" popup
    // could show stale/zero GST% while the grid right behind it showed
    // the correct dynamic numbers. Mirror calculateLineTotals()'s
    // precedence here too: prefer calculatedAmounts[idx] whenever it has
    // actually resolved to something, only falling back to the row's
    // saved percent for rows nothing has resolved yet.
    const calcState = calculatedAmounts[idx];
    const calcHasNonZeroGst =
      calcState &&
      ((Number(calcState.cgst) || 0) !== 0 ||
        (Number(calcState.sgst) || 0) !== 0 ||
        (Number(calcState.igst) || 0) !== 0);

    const cgst = Number(calcHasNonZeroGst ? calcState.cgst : (row.cgst_percent ?? 0));
    const sgst = Number(calcHasNonZeroGst ? calcState.sgst : (row.sgst_percent ?? 0));
    const igst = Number(calcHasNonZeroGst ? calcState.igst : (row.igst_percent ?? 0));

    const product_cost = Number(currentCost || 0);
    const totalCost = requirement_qty * product_cost;
    const netPriceAfterDiscount = totalCost - (totalCost * discount) / 100;

    const cgstAmount = (netPriceAfterDiscount * cgst) / 100;
    const sgstAmount = (netPriceAfterDiscount * sgst) / 100;
    const igstAmount = (netPriceAfterDiscount * igst) / 100;

    // Prefer the dynamically resolved taxes[] sitting in calculatedAmounts
    // (Basic Customs Duty / Social Welfare Surcharge / Other Customs
    // Charges etc. for an Import line, freshly re-resolved since the modal
    // opened); then the row's own saved taxes[]; then fall back to the
    // Product tab's current column labels if neither is available.
    const sourceTaxes = calcHasNonZeroGst && Array.isArray(calcState.taxes) && calcState.taxes.length
      ? calcState.taxes
      : row.taxes;

    const taxesArr = Array.isArray(sourceTaxes) && sourceTaxes.length
      ? sourceTaxes.map(t => ({
          type: t.type,
          percent: Number(t.percent) || 0,
          value: +((netPriceAfterDiscount * (Number(t.percent) || 0)) / 100).toFixed(2),
        }))
      : [
          { type: gstColumnLabels.col1 || "CGST", percent: cgst, value: cgstAmount },
          gstColumnLabels.col2 ? { type: gstColumnLabels.col2, percent: sgst, value: sgstAmount } : null,
          gstColumnLabels.col3 ? { type: gstColumnLabels.col3, percent: igst, value: igstAmount } : null,
        ].filter(Boolean);

    const totalGST = taxesArr.reduce((sum, t) => sum + (Number(t.value) || 0), 0);
    const finalPriceAfterGST =
      Number(row.final_price_after_gst) > 0
        ? Number(row.final_price_after_gst)
        : netPriceAfterDiscount + totalGST;

    setProductDetails({
      product_code: row.product_code,
      discount, product_cost, requirement_qty, hsnCode, cgst, sgst, igst,
      totalCost, netPriceAfterDiscount, cgstAmount, sgstAmount, igstAmount,
      totalGST, finalPriceAfterGST, taxes: taxesArr,
    });

    setCalculatedAmounts(prev => ({
      ...prev,
      [idx]: {
        totalCost, netPriceAfterDiscount, cgst, sgst, igst,
        cgstAmount, sgstAmount, igstAmount, totalGST, finalPriceAfterGST, hsnCode
      }
    }));

    setShowProductModal(true);
  } catch (error) {
    console.error("Error fetching product details:", error);
    toast.error("Failed to load product details.");
  }
};

  const handleDiscountChange = (e, idx) => {
    const newDiscounts = { ...discounts };
    newDiscounts[idx] = parseFloat(e.target.value) || 0;
    setDiscounts(newDiscounts);
  };

  const handleCheckboxChange = (e, idx) => {
    const isChecked = e.target.checked;
    setSelectedRows(prevSelected => {
      if (isChecked) {
        return [...prevSelected, idx];
      } else {
        return prevSelected.filter(i => i !== idx);
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const selectableIndices = modalData
        .map((row, idx) => {
          const s = String(row.review || "").toUpperCase();
          return ["APPROVED", "REJECTED"].includes(s) ? null : idx;
        })
        .filter(idx => idx !== null);
      setSelectedRows(selectableIndices);
    } else {
      setSelectedRows([]);
    }
  };


  // PO is fully finalized when every line has been decided (APPROVED or REJECTED)
  const isPOFinalized =
    Array.isArray(modalData) &&
    modalData.length > 0 &&
    modalData.every(row =>
      ["APPROVED", "REJECTED"].includes(
        String(row.review || "").toUpperCase()
      )
    );


  const handleSubmit = async () => {
    if (!modalData || modalData.length === 0) {
      toast.error("No PO data found");
      return;
    }

    if (isPOFinalized) {
      toast.warning("This Purchase Order is already finalized");
      return;
    }

    if (selectedRows.length === 0) {
      toast.warning("Please select at least one line item to proceed");
      return;
    }

    if (!comment.trim()) {
      toast.warning("Comment is mandatory");
      return;
    }

    if (
      actionType === "APPROVED" &&
      (!quoteNo || !quoteDate)
    ) {
      toast.warning("Quote No and Quote Date are mandatory");
      return;
    }

    const updatedRows = selectedRows.map((idx) => {
      const row = modalData[idx];

      return {
        id: row.id,
        purchase_order_id: row.purchase_order_id,
        product_code: row.product_code,
        purchase_request_no: row.purchase_request_no,
        status: actionType.toUpperCase(),
        comment,
        reviewed_by: getCurrentUser(),
      };
    });

    try {
      console.log({
        rows: updatedRows,
        quote_no: quoteNo,
        quote_date: quoteDate,
      });

      const response = await fetch(
        `${IMPORT_PO_API}/updateStatus`,
        {
          method: "POST",
          headers: { ...authHeaders(), 
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            rows: updatedRows,
            quote_no: quoteNo || null,
            quote_date: quoteDate || null,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(result.message);

        setCommentModal(false);
        setComment("");

        setSelectedRows([]);

        const poId = modalData[0]?.purchase_order_id;

        if (poId) {
          try {
            const refreshRes = await axios.get(`${IMPORT_PO_API}/${poId}`, { headers: authHeaders() });

            if (
              Array.isArray(refreshRes.data) &&
              refreshRes.data.length > 0
            ) {
              setModalData(refreshRes.data);
            }
          } catch (refreshErr) {
            console.error(
              "Error refreshing PO line items:",
              refreshErr
            );
          }
        }

        await refreshPOList();
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating PO status");
    }
  };

  useEffect(() => {
    if (modalData && Array.isArray(modalData)) {
      setConfirmationData(modalData);
    }
  }, [modalData]);

  useEffect(() => {
    if (modalData && modalData.length > 0) {
      setConfirmQtys(Array(modalData.length).fill(''));
      setConfirmDates(Array(modalData.length).fill(''));
      setDeliverQtys(Array(modalData.length).fill(''));
      setDeliverDates(Array(modalData.length).fill(''));
      setReceivingDates(Array(modalData.length).fill(''));
      setTransportationDates(Array(modalData.length).fill(''));
      setPackingDates(Array(modalData.length).fill(''));
      setPickingDates(Array(modalData.length).fill(''));
      setMadDates(Array(modalData.length).fill(''));
      setConfirmQtys(modalData.map(row => row.confirm_qty || ""));
      setConfirmDates(modalData.map(row => row.confirm_date ? formatDate(row.confirm_date) : ""));
      setDeliverQtys(modalData.map(row => row.deliver_qty || ""));
      setDeliverDates(modalData.map(row => row.deliver_date ? formatDate(row.deliver_date) : ""));
      setReceivingDates(modalData.map(row => row.receiving_date ? formatDate(row.receiving_date) : ""));
      setTransportationDates(modalData.map(row => row.transportation_date ? formatDate(row.transportation_date) : ""));
      setPackingDates(modalData.map(row => row.packing_date ? formatDate(row.packing_date) : ""));
      setPickingDates(modalData.map(row => row.picking_date ? formatDate(row.picking_date) : ""));
      setMadDates(modalData.map(row => row.mad ? formatDate(row.mad) : ""));
    }
  }, [modalData]);
//eslint-disable-next-line
  const handleConfirmQtyChange = (e, idx) => {
    const value = e.target.value;
    const updatedConfirmQtys = [...confirmQtys];
    updatedConfirmQtys[idx] = value;
    setConfirmQtys(updatedConfirmQtys);
  };

  const refreshPOList = async () => {
    try {
      const res = await axios.get(`${IMPORT_PO_API}/import-purchase-orders`, { headers: authHeaders() });
      if (res.data.success) {
        setAllPurchaseOrders(res.data.purchaseOrders);
        setPoList(res.data.purchaseOrders);
        setPurchaseOrders(res.data.purchaseOrders);
      }
    } catch (err) {
      console.error("Error refreshing PO list:", err);
    }
  };
//eslint-disable-next-line
  const handleConfirmDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedConfirmDates = [...confirmDates];
    updatedConfirmDates[idx] = value;
    setConfirmDates(updatedConfirmDates);
  };
//eslint-disable-next-line
  const handleDeliverQtyChange = (e, idx) => {
    const value = e.target.value;
    const updatedDeliverQtys = [...deliverQtys];
    updatedDeliverQtys[idx] = value;
    setDeliverQtys(updatedDeliverQtys);
  };
//eslint-disable-next-line
  const handleDeliverDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedDeliverDates = [...deliverDates];
    updatedDeliverDates[idx] = value;
    setDeliverDates(updatedDeliverDates);
  };
//eslint-disable-next-line
  const handleReceivinDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedReceivingDates = [...receivingDates];
    updatedReceivingDates[idx] = value;
    setReceivingDates(updatedReceivingDates);
  };
//eslint-disable-next-line
  const handleTransportationDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedTransportationDates = [...transportationDates];
    updatedTransportationDates[idx] = value;
    setTransportationDates(updatedTransportationDates);
  };

  const handleProductFieldChange = (idx, field, value) => {
    const updated = [...editableProducts];
    updated[idx][field] = value;
    setEditableProducts(updated);
  };

  // 🆕 Fires when the Unit Cost box loses focus. Whatever number is
  // sitting in the box at that moment is treated as the amount the user
  // just typed — if a foreign Currency + Exchange Rate is active on the
  // Customs tab, it's multiplied by that rate right there in the box
  // (rate is "1 INR = <rate> foreign", e.g. 1 INR = 0.071 Yuan, so
  // typed INR * rate = foreign amount). base_cost_inr is stored as the
  // pre-conversion (typed) figure so re-picking a country later
  // re-converts from what you actually entered, not a stale value.
  const handleProductCostBlur = (idx) => {
    const rate = Number(importExchangeRate) || 0;
    const typed = Number(editableProducts[idx]?.product_cost) || 0;

    if (poOrderType !== "import" || !importCurrency || !rate) return; // nothing to convert

    const updated = [...editableProducts];
    updated[idx] = {
      ...updated[idx],
      base_cost_inr: typed,
      product_cost: typed * rate
    };
    setEditableProducts(updated);
  };

//eslint-disable-next-line
  const handlePackingDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedPackingDates = [...packingDates];
    updatedPackingDates[idx] = value;
    setPackingDates(updatedPackingDates);
  };
//eslint-disable-next-line
  const handlePickingrDateChange = (e, idx) => {
    const value = e.target.value;
    const updatedPickingDates = [...pickingDates];
    updatedPickingDates[idx] = value;
    setPickingDates(updatedPickingDates);
  };
//eslint-disable-next-line
  const handleMadChange = (e, idx) => {
    const value = e.target.value;
    const updatedMadDates = [...madDates];
    updatedMadDates[idx] = value;
    setMadDates(updatedMadDates);
  };

  // ── Characteristics Modal Handlers ─────────────────────────────
  // classSource: "product" filters to the Product Class's own characteristics
  // (e.g. Supplier Description); "purchase" filters to the Purchase Class's
  // own characteristics (e.g. Under/Over Tolerance). Falls back to showing
  // everything if we don't have the class's characteristic list yet, so the
  // modal never appears empty while classCharacteristicMap is still loading.
  const handleOpenCharModal = (idx, classSource = "product") => {
    setCharModalRowIdx(idx);
    setCharModalSource(classSource);
    const row = modalData[idx];
    const chars = row.characteristics || [];

    const classCode =
      classSource === "purchase"
        ? row.purchase_class || allProducts.find(p => p.productCode === row.product_code)?.purchaseClass
        : row.product_class || allProducts.find(p => p.productCode === row.product_code)?.productClass;

    const allowedNames = classCode ? classCharacteristicMap[classCode] : null;

    const filteredChars = allowedNames
      ? chars.filter(c => allowedNames.has((c.characteristic || "").trim().toLowerCase()))
      : chars;

    setSelectedCharacteristics(
      filteredChars.map(c => ({ characteristic: c.characteristic, value: c.value || "" }))
    );
    setModalVisible(true);
  };

  const onModalCharChange = (charIdx, value) => {
    const updated = [...selectedCharacteristics];
    updated[charIdx] = { ...updated[charIdx], value };
    setSelectedCharacteristics(updated);
  };

  const handleSaveCharModal = () => {
    const updated = [...modalData];
    const existingChars = updated[charModalRowIdx]?.characteristics || [];

    // Merge the edited subset back into the row's full characteristics list
    // (rather than overwriting it) so editing Purchase Class's Under/Over
    // Tolerance here doesn't wipe out Product Class's Supplier Description
    // that lives in the same row, and vice versa.
    const editedNames = new Set(
      selectedCharacteristics.map(c => (c.characteristic || "").trim().toLowerCase())
    );
    const untouched = existingChars.filter(
      c => !editedNames.has((c.characteristic || "").trim().toLowerCase())
    );

    updated[charModalRowIdx] = {
      ...updated[charModalRowIdx],
      characteristics: [...untouched, ...selectedCharacteristics]
    };
    setModalData(updated);
    setModalVisible(false);
  };
  // ────────────────────────────────────────────────────────────────

  const handleShortClose = async () => {
    if (!window.confirm("Are you sure you want to Short Close this PO? It will not proceed to GRN.")) return;
    try {
      const res = await axios.post(`${IMPORT_PO_API}/short-close`, {
        purchase_order_id: modalData?.[0]?.purchase_order_id
      }, { headers: authHeaders() });
      if (res.data.success) {
        toast.success("PO Short Closed successfully.");
        closeModal();
        setListTab("short_closed");
        // Refresh PO list
        axios.get(`${IMPORT_PO_API}/import-purchase-orders`, { headers: authHeaders() }).then(r => {
          if (r.data.success) {
            setAllPurchaseOrders(r.data.purchaseOrders);
            setPoList(r.data.purchaseOrders);
            setPurchaseOrders(r.data.purchaseOrders);
          }
        });
      } else {
        toast.error(res.data.message || "Short Close failed.");
      }
    } catch (err) {
      toast.error("Short Close failed.");
    }
  };

  const handleSaveConfirmation = async () => {
    try {
      const payload = {
        purchase_order_id: modalData?.[0]?.purchase_order_id,
        confirmation: confirmationLines
      };
      const res = await axios.post(`${IMPORT_PO_API}/save-confirmation`, payload, { headers: authHeaders() });
      if (res.data.success) {
        toast.success("Confirmation saved successfully.");
      } else {
        toast.error(res.data.message || "Failed to save confirmation.");
      }
    } catch (err) {
      toast.error("Failed to save confirmation.");
    }
  };

  const handleSubmitPO = async () => {
    const poId = modalData?.[0]?.purchase_order_id;
    if (!poId) {
      toast.error("No Purchase Order found to submit");
      return;
    }

    try {
      const response = await fetch(
        `${IMPORT_PO_API}/submit/${poId}`,
        {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(result.message || "Purchase Order submitted for approval");
      } else {
        // Whatever the reason (already submitted by a near-simultaneous
        // click, status changed elsewhere, etc.), the screen we're
        // showing no longer matches the real PO — resync below instead
        // of leaving the stale CREATED-looking form on screen.
        toast.error(result.message || "Failed to submit Purchase Order");
      }
      refreshPOList();
      await viewPO(poId);
    } catch (err) {
      console.error("SUBMIT PO ERROR:", err);
      toast.error("Failed to submit Purchase Order");
    }
  };

  const handleSave = (onSuccess) => {
    if (!modalData || modalData.length === 0) {
      toast.error("No Purchase Order data");
      return;
    }


    if (!selectedVendor && !vendorDetails?.vendor_name) {
      toast.info("Please assign a Vendor before saving the Purchase Order");
      return;
    }

    if (!vendorDetails) {
      toast.error("Vendor details are missing. Please reselect the vendor.");
      return;
    }

    // =================================================
// DELIVERY DATE MANDATORY VALIDATION
// =================================================
const missingDeliveryDateRow = modalData.findIndex(
  (_, idx) => {
    const deliveryDate = editableProducts[idx]?.delivery_date;
    return !deliveryDate || deliveryDate.trim() === "";
  }
);

if (missingDeliveryDateRow !== -1) {
  toast.error(
    `Delivery Date is mandatory for Product ${
      modalData[missingDeliveryDateRow]?.product_code ||
      `Row ${missingDeliveryDateRow + 1}`
    }`
  );
  return;
}

    // =================================================
    // 🆕 QTY-WITHIN-TOLERANCE-RANGE VALIDATION
    // Block save if the entered Qty falls outside the Under/Over
    // Tolerance range shown in the Range column.
    // =================================================
    for (let idx = 0; idx < modalData.length; idx++) {
      const row = modalData[idx];
      const { rangeMin, rangeMax } = getToleranceRange(row, idx);

      // Only enforce when a range is actually defined for this row
      // (i.e. the Product Class has Over/Under Tolerance % set).
      if (rangeMin === null && rangeMax === null) continue;

      const enteredQty = Number(editableProducts[idx]?.requirement_qty);

      if (
        isNaN(enteredQty) ||
        (rangeMin !== null && enteredQty < rangeMin) ||
        (rangeMax !== null && enteredQty > rangeMax)
      ) {
        // 🆕 Build the message from whichever bound(s) are actually set,
        // instead of assuming both exist (rangeMin/rangeMax can now be
        // null individually — see getToleranceRange).
        const rangeMsg =
          rangeMin !== null && rangeMax !== null
            ? `${rangeMin.toFixed(2)} - ${rangeMax.toFixed(2)}`
            : rangeMax !== null
              ? `≤ ${rangeMax.toFixed(2)}`
              : `≥ ${rangeMin.toFixed(2)}`;

        toast.error(
          `Qty for ${row.product_code || `Row ${idx + 1}`} must be within the allowed range ` +
          `(${rangeMsg}).`
        );
        return;
      }
    }

    /* Block save if products belong to different default vendors */
    const pirVendors = [...new Set(
      modalData.map(r => r._pirVendorName).filter(Boolean)
    )];
    if (pirVendors.length > 1) {
      toast.error(
        `Cannot save PO: Products have different default vendors (${pirVendors.join(", ")}). ` +
        `A Purchase Order must have a single vendor.`
      );
      return;
    }

    const calculateGST = ({ qty, unitCost, discount, cgst, sgst, igst }) => {
      const totalCost = qty * unitCost;
      const netAfterDiscount = totalCost - (totalCost * discount) / 100;

      const cgstAmount = (netAfterDiscount * cgst) / 100;
      const sgstAmount = (netAfterDiscount * sgst) / 100;
      const igstAmount = (netAfterDiscount * igst) / 100;

      const totalGST = cgstAmount + sgstAmount + igstAmount;
      const finalPrice = netAfterDiscount + totalGST;

      return {
        totalCost,
        netAfterDiscount,
        cgstAmount,
        sgstAmount,
        igstAmount,
        totalGST,
        finalPrice,
      };
    };

    const payload = {

      purchase_order_id:
        modalData?.[0].purchase_order_id,
      quote_no: quoteNo || null,
      quote_date: quoteDate || null,

      modified_by:
        getCurrentUser(),
      created_by: getCurrentUser(),
      created_date: poCreatedDate || getTodayLocal(),
      product_type: poProductType || null,

      // 🆕 Order Type + Import PO header fields
      order_type: poOrderType || "domestic",
      supplier_country: poOrderType === "import" ? (importSupplierCountry || null) : null,
      import_currency: poOrderType === "import" ? (importCurrency || null) : null,
      exchange_rate: poOrderType === "import" ? (importExchangeRate || null) : null,
      incoterm: poOrderType === "import" ? (importIncoterm || null) : null,
      port_of_loading: poOrderType === "import" ? (importPortOfLoading || null) : null,
      port_of_discharge: poOrderType === "import" ? (importPortOfDischarge || null) : null,
      final_destination: poOrderType === "import" ? (importFinalDestination || null) : null,
      expected_shipment_date: poOrderType === "import" ? (importExpectedShipmentDate || null) : null,
      expected_arrival_date: poOrderType === "import" ? (importExpectedArrivalDate || null) : null,
      lc_required: poOrderType === "import" ? importLcRequired : null,
      insurance_required: poOrderType === "import" ? importInsuranceRequired : null,


      purchase_organization:
        modalData?.[0]?.purchase_organization || "",

      purchase_planner:
        modalData?.[0]?.purchase_planner || "",

      location:
        poLocation,


      vendor_name:
        vendorDetails?.business_partner_name ||
        vendorDetails?.vendor_name ||
        modalData?.[0]?.vendor_name ||
        null,

      // ✅ Persist the actual vendor/supplier code too — vendor_name alone
      // isn't enough because on reload the PO is re-linked to a vendor
      // using vendor_code first (see load logic around vendorCode/vendor),
      // so without this the supplier change appears to "not save".
      vendor_code:
        selectedVendor || null,

      contact_person_name:
        vendorDetails?.contact_person_name ||
        modalData?.[0]?.contact_person ||
        "",

      business_type:
        vendorDetails?.business_type ||
        modalData?.[0]?.business_type ||
        "",

      phone:
        vendorDetails?.phone ||
        vendorDetails?.contact_details ||
        modalData?.[0]?.phone ||
        "",

      gstin:
        vendorDetails?.gstin ||
        modalData?.[0].gstin ||
        null,

      registered_address:
        vendorDetails?.registered_address ||
        modalData?.[0].address ||
        null,

      payment_terms: paymentTerms || null,

      mode_of_transport: modeOfTransport || null,
      delivery_type: deliveryType || null,
      freight_terms: freightTerms || null,
      insurance_terms: insuranceTerms || null,
      transporter: transporter || null,
      po_header_lot_type: poHeaderLotType || null,
      productDetails: modalData.map((row, idx) => {

        // 🆕 Landed Cost tab — flatten this row's entry into landed_*
        // fields expected by purchaseOrder.service.js. Manual entry only;
        // blank/empty stays null rather than being coerced to 0.
        // Per-line-item groups (Customs) use this row's own entry;
        // PO-level groups (Port/Clearing, Bank) use the single "po_level"
        // entry and are written identically onto every line item, since
        // there is one shared value entered once for the whole PO.
        const landedCostRowKey = row.id || `idx_${idx}`;
        const landedCostEntry = landedCosts[landedCostRowKey] || {};
        const poLevelLandedCostEntry = landedCosts["po_level"] || {};
        const landedCostFields = {};
        LANDED_COST_GROUPS.forEach(g => g.fields.forEach(f => {
          const source = g.perLineItem === false ? poLevelLandedCostEntry : landedCostEntry;
          const val = source[f.key];
          landedCostFields[`landed_${f.key}`] =
            (val === "" || val === null || val === undefined) ? null : Number(val);
        }));

        const qty =
          Number(
            editableProducts[idx]?.requirement_qty || 0
          );

        const unitCost =
          Number(
            editableProducts[idx]?.product_cost || 0
          );

        const discount =
          Number(
            discounts[idx] || 0
          );

        const gstState =
          calculatedAmounts[idx] || {};

        // Fall back to row.cgst_percent / sgst_percent / igst_percent
        // when calculatedAmounts hasn't been populated yet (e.g. on Create
        // before the user clicks "View"). This mirrors calculateLineTotals()
        // so the saved values always match what is shown on screen.
        const cgst =
          Number(gstState.cgst ?? row.cgst_percent ?? 0);

        const sgst =
          Number(gstState.sgst ?? row.sgst_percent ?? 0);

        const igst =
          Number(gstState.igst ?? row.igst_percent ?? 0);

        const calc =
          calculateGST({
            qty,
            unitCost,
            discount,
            cgst,
            sgst,
            igst
          });

        const toleranceRange = getToleranceRange(row, idx);

        return {

          id:
            row.id || null,

          product_code:
            row.product_code,

          product_class:
            row.product_class || null,

          description:
            row.description ||
            row.product_description ||
            null,

          uom:
            row.uom || "",

          currency:
            row.currency || "",

          purchase_request_no:
            row.purchase_request_no,

          product_cost:
            unitCost,

          requirement_qty:
            qty,

          delivery_date:
            editableProducts[idx]?.delivery_date || null,

          discount:
            discount,

          total_cost:
            calc.totalCost,

          net_price_after_discount:
            calc.netAfterDiscount,

          hsn_code:
            gstState.hsnCode || row.hsn_code || null,

          cgst_percent:
            cgst,

          sgst_percent:
            sgst,

          igst_percent:
            igst,

          cgst_amount:
            calc.cgstAmount,

          sgst_amount:
            calc.sgstAmount,

          igst_amount:
            calc.igstAmount,

          total_gst_amount:
            calc.totalGST,

          final_price_after_gst:
            calc.finalPrice,

          confirm_qty:
            confirmQtys[idx] || null,

          confirm_date:
            confirmDates[idx] || null,

          deliver_qty:
            deliverQtys[idx] || null,

          deliver_date:
            deliverDates[idx] || null,

          receiving_date:
            receivingDates[idx] || null,

          transportation_date:
            transportationDates[idx] || null,

          packing_date:
            packingDates[idx] || null,

          picking_date:
            pickingDates[idx] || null,

          mad:
            madDates[idx] || null,

          characteristics:
            row.characteristics || [],

          range_min:
            toleranceRange.rangeMin,

          range_max:
            toleranceRange.rangeMax,

          pir_number:
            row.pir_number || null,

          // 🆕 Import PO — item-level fields (only meaningful when Order
          // Type = Import PO). Country Of Origin always mirrors the PO
          // header's Supplier Country now — no separate per-line value.
          country_of_origin:
            poOrderType === "import" ? (importSupplierCountry || null) : null,

          // 🆕 Import PO — Landed Cost tab (17 fields)
          ...landedCostFields,

          // 🆕 Dynamic tax breakup, same shape as elsewhere in this file
          // (gstColumnLabels-driven — CGST/SGST/IGST for domestic,
          // whatever the GST Configuration screen defines for Import,
          // e.g. IGST/BCD/SWS). This was missing from the save payload
          // entirely, so custom_purchase_order_tax never got written.
          taxes: [
            { type: gstColumnLabels.col1 || "CGST", percent: cgst, value: calc.cgstAmount },
            gstColumnLabels.col2 ? { type: gstColumnLabels.col2, percent: sgst, value: calc.sgstAmount } : null,
            gstColumnLabels.col3 ? { type: gstColumnLabels.col3, percent: igst, value: calc.igstAmount } : null,
          ].filter(Boolean),
        };

      }),

      deletedLineItemIds: deletedLineItemIds.filter(id => id != null),
    };

    const isManualPO =
      !modalData?.[0]?.purchase_order_id;

    const apiUrl =
      isManualPO
        ? `${IMPORT_PO_API}/create-purchase-order`
        : `${IMPORT_PO_API}/update-purchase-order`;

    return fetch(apiUrl, {
      method: "POST",
      headers: { ...authHeaders(),  "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(async (result) => {
        if (result.success) {
          refreshPOList();
          setIsPoSaved(true);
          // 🆕 First-ever save into custom_purchase_orders for this PO —
          // switch to the normal Edit ⇄ Save toggle from here on instead
          // of the always-editable single-Save state. (isEditMode itself
          // is left to the existing logic just below / the onSuccess
          // caller, and gets recomputed correctly on the viewPO reload.)
          setHasCustomData(true);
          const savedPoId =
            result.purchase_order_id ||
            modalData?.[0]?.purchase_order_id;

          if (typeof onSuccess === "function") {
            // Caller (e.g. Submit on a CREATED PO) handles its own
            // success messaging / follow-up instead of the plain
            // "saved" toast, and decides whether edit mode stays on.
            await onSuccess(savedPoId);
          } else {
            toast.success("Purchase Order saved successfully");
            setIsEditMode(false);
            if (savedPoId) {
              await viewPO(savedPoId);
            }
          }
        } else {
          alert("Failed to save PO: " + result.message);
        }
      })
      .catch((err) => {
        console.error("SAVE ERROR:", err);
        toast.error("Error while saving Purchase Order");
      });
  };

  const handleApprove = () => {
    setComment("");
    setActionType("APPROVED");
    setCommentModal(true);
  };

  const handleReject = () => {
    setComment("");
    setActionType("REJECTED");
    setCommentModal(true);
  };

  const handleQuery = () => {
    setComment("");
    setActionType("QUERY");
    setCommentModal(true);
  };


 // ── Raw Material Tolerance Range (Qty ± Under/Over Tolerance %) ──
 // Under Tolerance %  → rangeMin = qty - (qty * underTolerance / 100)
 // Over Tolerance %   → rangeMax = qty + (qty * overTolerance / 100)
 // Values come from the "Under Tolerance" / "Over Tolerance" characteristics
 // on the PO line (entered via the Characteristics modal), which are only
 // present when the product's class defines those calculation types.
 const getToleranceRange = (row, idx) => {
  // 🆕 Range must be anchored to the ORIGINAL/base qty (row.requirement_qty,
  // as loaded from the DB), NOT the value currently being typed into the
  // input (editableProducts). Using the edited value here would make the
  // range recalculate around whatever the user types, so it could never
  // actually be "out of range" — defeating the whole point of the check.
  const qty = Number(
    row.requirement_qty ??
    0
  );

  const chars = row.characteristics || [];
  // Space-insensitive match: some Product Classes have this saved as
  // "OverTolerance" (no space) instead of "Over Tolerance" — treat both
  // the same rather than requiring every class to be renamed.
  const findChar = (name) =>
    chars.find(c => (c.characteristic || "").trim().toLowerCase().replace(/\s+/g, "") === name);

  const underChar = findChar("undertolerance");
  const overChar = findChar("overtolerance");

  const underPct = Number(underChar?.value);
  const overPct = Number(overChar?.value);

  const hasUnder = underChar && !isNaN(underPct) && underChar.value !== "";
  const hasOver = overChar && !isNaN(overPct) && overChar.value !== "";

  if (!qty || (!hasUnder && !hasOver)) {
    return { rangeMin: null, rangeMax: null, rangeText: "" };
  }

  // 🆕 Only set the bound that's actually defined — defaulting the missing
  // side to `qty` made a lone Over Tolerance render as a fake full range
  // ("25.00 - 26.25") and would've also wrongly enforced `qty` as a lower
  // bound in the qty-within-range validation below even with no Under
  // Tolerance configured.
  const rangeMin = hasUnder ? qty - (qty * underPct) / 100 : null;
  const rangeMax = hasOver ? qty + (qty * overPct) / 100 : null;

  let rangeText = "";
  if (hasUnder && hasOver) {
    // If Under/Over Tolerance % was entered as 0 (or effectively cancels out),
    // rangeMin === rangeMax and showing "25.00 - 25.00" is a fake range.
    // Collapse to a single value instead — same fix as Sales Order/Enquiry's
    // collapseIfSameRange — keep all three in sync.
    rangeText = rangeMin === rangeMax
      ? rangeMin.toFixed(2)
      : `${rangeMin.toFixed(2)} - ${rangeMax.toFixed(2)}`;
  } else if (hasOver) {
    rangeText = `≤ ${rangeMax.toFixed(2)}`;
  } else if (hasUnder) {
    rangeText = `≥ ${rangeMin.toFixed(2)}`;
  }

  return {
    rangeMin,
    rangeMax,
    rangeText
  };
 };

 const calculateLineTotals = (row, idx) => {
  const qty = Number(
    editableProducts[idx]?.requirement_qty ??
    row.requirement_qty ??
    0
  );

  const unitCost = Number(
    editableProducts[idx]?.product_cost ??
    row.product_cost ??
    0
  );

  const discount = Number(
    discounts[idx] ??
    row.discount ??
    0
  );

  // 🆕 Only trust calculatedAmounts' GST% if it actually resolved to
  // something non-zero. If cgst/sgst/igst are ALL zero there (whether
  // never set, or set by a server call that legitimately/silently came
  // back empty — no gst_configuration match, vendor mismatch, etc.), fall
  // back to the row's own cgst_percent/sgst_percent/igst_percent instead —
  // the exact same source the "View" popup already uses for unsaved rows.
  // Without this, a nullish-coalesce (`?? `) treats a real 0 from
  // calculatedAmounts as "present" and never falls through, so the table
  // could get stuck showing 0.00 while View (which doesn't depend on that
  // same server round trip) kept showing the correct %.
  const calc = calculatedAmounts[idx];
  const calcHasNonZeroGst =
    calc &&
    ((Number(calc.cgst) || 0) !== 0 ||
      (Number(calc.sgst) || 0) !== 0 ||
      (Number(calc.igst) || 0) !== 0);

  const cgstP = Number(
    calcHasNonZeroGst ? calc.cgst : (row.cgst_percent ?? 0)
  );

  const sgstP = Number(
    calcHasNonZeroGst ? calc.sgst : (row.sgst_percent ?? 0)
  );

  const igstP = Number(
    calcHasNonZeroGst ? calc.igst : (row.igst_percent ?? 0)
  );

  const totalCost = qty * unitCost;

  const netPrice =
    totalCost - (totalCost * discount) / 100;

  const cgstAmt =
    (netPrice * cgstP) / 100;

  const sgstAmt =
    (netPrice * sgstP) / 100;

  const igstAmt =
    (netPrice * igstP) / 100;

  const totalGST =
    cgstAmt + sgstAmt + igstAmt;

  const finalPrice =
    netPrice + totalGST;

  return {
    grossTotal: totalCost,
    netPrice,
    cgstAmt,
    sgstAmt,
    igstAmt,
    totalGST,
    finalPrice
  };
};

  const totals = (modalData && Array.isArray(modalData))
  ? modalData.reduce((acc, row, idx) => {
      const calc = calculateLineTotals(row, idx);

      return {
        gross: acc.gross + calc.grossTotal,
        net: acc.net + calc.netPrice,
        cgst: acc.cgst + calc.cgstAmt,
        sgst: acc.sgst + calc.sgstAmt,
        igst: acc.igst + calc.igstAmt,
        totalGst: acc.totalGst + calc.totalGST,
        grand: acc.grand + calc.finalPrice
      };
    }, {
      gross: 0,
      net: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      totalGst: 0,
      grand: 0
    })
  : {
      gross: 0,
      net: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      totalGst: 0,
      grand: 0
    };

  // 🆕 Port/Clearing + Bank total — both are PO-level (single row, key
  // "po_level" in landedCosts), same shape as the groupSubtotal calc used
  // inside the Customs/Port-Clearing/Bank tabs themselves (search
  // "groupSubtotal"). Summed here so the Product tab's totals row can show
  // it under Grand Total without duplicating that per-tab logic.
  const landedPoLevelEntry = landedCosts["po_level"] || {};
  const portClearingTotal = (LANDED_COST_GROUPS.find(g => g.tabId === "landed_port_clearing")?.fields || [])
    .reduce((sum, f) => sum + (Number(landedPoLevelEntry[f.key]) || 0), 0);
  const bankTotal = (LANDED_COST_GROUPS.find(g => g.tabId === "landed_bank")?.fields || [])
    .reduce((sum, f) => sum + (Number(landedPoLevelEntry[f.key]) || 0), 0);

  const formatCurrency = (value) => {
    const val = Number(value);
    if (isNaN(val)) return "0";
    return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // 🆕 Payment History should only be based on products that have actually
  // been APPROVED at the line level (row.review === "APPROVED") — not the
  // full PO total. Mirrors the same filter used on the Confirmation tab.
  const approvedTotals = (modalData && Array.isArray(modalData))
    ? modalData
        .map((row, idx) => ({ row, idx }))
        .filter(({ row }) => String(row.review || "").toUpperCase() === "APPROVED")
        .reduce((acc, { row, idx }) => {
          const calc = calculateLineTotals(row, idx);
          return {
            gross: acc.gross + calc.grossTotal,
            net: acc.net + calc.netPrice,
            cgst: acc.cgst + calc.cgstAmt,
            sgst: acc.sgst + calc.sgstAmt,
            igst: acc.igst + calc.igstAmt,
            totalGst: acc.totalGst + calc.totalGST,
            grand: acc.grand + calc.finalPrice
          };
        }, { gross: 0, net: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0, grand: 0 })
    : { gross: 0, net: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0, grand: 0 };

// 🆕 Populate the form above the table with an existing payment row's
// values so the user can edit it, and switch the form into "edit mode".
const handleEditPaymentClick = (row) => {
  setEditingPaymentId(row.id);
  setPaymentHistoryTabType(row.payment_type || row.paymentType || "Advance");
  setPaymentHistoryTabDate(
    row.payment_date ? String(row.payment_date).slice(0, 10) : paymentHistoryTabDate
  );
  setPaymentHistoryTabAmount(String(row.advance ?? row.amount ?? ""));
  setPaymentHistoryTabDescription(row.payment_description || row.paymentDescription || "");
  setPaymentHistoryTabReference(row.reference_number || row.referenceNumber || "");
};

// 🆕 Leave edit mode without saving changes.
const handleCancelEditPayment = () => {
  setEditingPaymentId(null);
  setPaymentHistoryTabAmount("");
  setPaymentHistoryTabDescription("");
  setPaymentHistoryTabReference("");
};

const handleAddPaymentRow = async () => {
  const poId = modalData?.[0]?.purchase_order_id;

  if (!poId) {
    console.log("PO ID missing");
    return;
  }

  if (!paymentHistoryTabAmount) return toast.error("Please enter an amount.");

  // 🆕 Use only the APPROVED-line total for payment calculations, not the
  // full PO total (unapproved/rejected lines shouldn't be billed against).
  const totalGrand = approvedTotals.grand;

  // When editing, exclude the row being edited from the "already paid"
  // total so the remaining-balance check reflects the amount being replaced.
  const currentTotalPaid = advancePayments.reduce((sum, p) => {
    if (editingPaymentId && p.id === editingPaymentId) return sum;
    return sum + Number(p.advance || p.amount || 0);
  }, 0);
  const remainingBalance = totalGrand - currentTotalPaid;

  if (remainingBalance <= 0) {
    return toast.error("No balance remaining. Payment is already complete.");
  }
  if (Number(paymentHistoryTabAmount) > remainingBalance) {
    return toast.error(
      `Amount cannot exceed remaining balance of ₹${formatCurrency(remainingBalance)}.`
    );
  }

  try {
    const res = editingPaymentId
      ? await axios.put(`${IMPORT_PO_API}/advance-payments/${poId}/${editingPaymentId}`, {
            total: totalGrand,
            advance: Number(paymentHistoryTabAmount),
            paymentType: paymentHistoryTabType,
            paymentDate: paymentHistoryTabDate,
            paymentDescription: paymentHistoryTabDescription,
            referenceNumber: paymentHistoryTabReference
          }, { headers: authHeaders() })
      : await axios.post(`${IMPORT_PO_API}/advance-payments/${poId}`, {
            total: totalGrand,
            advance: Number(paymentHistoryTabAmount),
            paymentType: paymentHistoryTabType,
            paymentDate: paymentHistoryTabDate,
            paymentDescription: paymentHistoryTabDescription,
            referenceNumber: paymentHistoryTabReference
          }, { headers: authHeaders() });

    if (res.data.success) {
      await fetchAdvancePayments(poId);
      setPaymentHistoryTabAmount("");
      setPaymentHistoryTabDescription("");
      setPaymentHistoryTabReference("");
      toast.success(editingPaymentId ? "Payment updated!" : "Payment recorded!");
      setEditingPaymentId(null);
    }
  } catch (err) {
    console.error("Full Error:", err);
    console.error("Response:", err.response?.data);
    console.error("Status:", err.response?.status);

    toast.error(editingPaymentId ? "Failed to update payment." : "Failed to add payment.");
  }
};
// 🆕 Discard unsaved edits to the box and restore it to the latest saved
// acknowledgement. Keeps editingAckId pointing at that saved row so the
// next Save still updates it (PUT) rather than creating a duplicate (POST).
const handleCancelEditAcknowledgement = () => {
  if (acknowledgements.length > 0) {
    const latest = acknowledgements.reduce((best, row) => {
      if (!best) return row;
      const bestTime = new Date(best.acknowledgement_date || 0).getTime();
      const rowTime = new Date(row.acknowledgement_date || 0).getTime();
      return rowTime >= bestTime ? row : best;
    }, null);
    setAckNoInput(latest.acknowledgement_no || "");
    setAckDateInput(
      latest.acknowledgement_date ? String(latest.acknowledgement_date).slice(0, 10) : ackDateInput
    );
    setEditingAckId(latest.id);
  } else {
    setEditingAckId(null);
    setAckNoInput("");
  }
};

// 🆕 Save (add or update) an acknowledgement row. Once saved, the PO list
// column flips from "Pending" to "Acknowledged" (derived server-side from
// whether any row exists for this PO).
const handleSaveAcknowledgement = async () => {
  const poId = modalData?.[0]?.purchase_order_id;

  if (!poId) {
    console.log("PO ID missing");
    return;
  }

  if (!ackNoInput) return toast.error("Please enter an Acknowledgement No.");

  try {
    const res = editingAckId
      ? await axios.put(`${IMPORT_PO_API}/acknowledgements/${poId}/${editingAckId}`, {
            acknowledgementNo: ackNoInput,
            acknowledgementDate: ackDateInput
          }, { headers: authHeaders() })
      : await axios.post(`${IMPORT_PO_API}/acknowledgements/${poId}`, {
            acknowledgementNo: ackNoInput,
            acknowledgementDate: ackDateInput
          }, { headers: authHeaders() });

    if (res.data.success) {
      const wasUpdate = !!editingAckId;
      await fetchAcknowledgements(poId); // 🆕 re-populates the box and editingAckId with the saved value
      toast.success(wasUpdate ? "Acknowledgement updated!" : "Acknowledgement saved!");
      // Keep the PO list's Acknowledgement column in sync without a full reload
      setPurchaseOrders(prev => prev.map(po =>
        po.purchase_order_id === poId ? { ...po, acknowledgement_status: "Acknowledged" } : po
      ));
      setPoList(prev => prev.map(po =>
        po.purchase_order_id === poId ? { ...po, acknowledgement_status: "Acknowledged" } : po
      ));
      setAllPurchaseOrders(prev => prev.map(po =>
        po.purchase_order_id === poId ? { ...po, acknowledgement_status: "Acknowledged" } : po
      ));
    }
  } catch (err) {
    console.error("Full Error:", err);
    console.error("Response:", err.response?.data);
    console.error("Status:", err.response?.status);

    toast.error(editingAckId ? "Failed to update acknowledgement." : "Failed to save acknowledgement.");
  }
};

//eslint-disable-next-line
  const handleDownloadPDF = async () => {
    if (!modalData || modalData.length === 0) return;
    const poId = modalData?.[0].purchase_order_id;
    const downloadUrl =
      `${IMPORT_PO_API}/download-pdf/${poId}?quote_no=${encodeURIComponent(quoteNo || "")}&quote_date=${quoteDate || ""}&generated_by=${encodeURIComponent(getCurrentUser())}`;

    try {
      toast.info("Generating PDF... Please wait.");
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error("Error downloading PDF", error);
      toast.error("Failed to download PDF.");
    }
  };



  // No role, or a role with no entry for PURCHASE_ORDER at all -> no access
  // to this screen. (Admin / no-role-assigned / any granted permission_type
  // all satisfy hasModuleAccess — see useFieldPermissions.)
  if (!permLoading && !hasModuleAccess) {
    return (
      <Layout>
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
          You don't have access to Purchase Orders.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <style>{`
        .po-field-tooltip[data-tooltip=""]::after,
        .po-field-tooltip:not([data-tooltip])::after {
          content: none;
        }
        .po-field-tooltip::after {
          content: attr(data-tooltip);
          position: absolute;
          left: 0;
          top: calc(100% + 6px);
          background: #1e293b;
          color: #fff;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          line-height: 1.4;
          white-space: normal;
          max-width: 320px;
          width: max-content;
          z-index: 100000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.18);
          opacity: 0;
          visibility: hidden;
          transform: translateY(-4px);
          transition: opacity 0.12s ease, transform 0.12s ease;
          pointer-events: none;
        }
        .po-field-tooltip:hover::after {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        @media (max-width: 768px) {
          .po-filter-card .row > div {
            flex: 0 0 100%;
            max-width: 100%;
          }
          .po-page-title { font-size: 20px !important; }
          .po-modal { padding: 10px !important; }
          .po-tabs { flex-wrap: wrap; }
          .po-tabs button { font-size: 12px !important; padding: 3px 7px !important; }
        }
        @media (max-width: 576px) {
          .po-modal-body { padding: 10px !important; }
          .po-vendor-row > div { flex: 0 0 100% !important; max-width: 100% !important; }
        }
        .po-table-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .po-modal-backdrop { overflow-y: auto; }
      `}</style>
      <div style={styles.pageTitleBar}>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >

          <div>
            <h2 style={styles.pageTitle} className="po-page-title">
              Custom
            </h2>

            <div style={styles.pageSubtitle}>
              Manage and view Import PO
            </div>
          </div>

          {canCreatePO && (
          <button
            className="btn btn-dark"
            onClick={() => {
              // Reset all carry-over state before opening a fresh Create modal
              setSelectedVendor("");
              setVendorDetails(null);
              setPoLocation("");

              setPaymentTermNumber("");
              setPaymentTermUnit("days");
              setDiscounts({});
              setCalculatedAmounts({});
              setIsPoSaved(false);
              setHasCustomData(false);
              setCreateActiveTab("basic");

              // ✅ ADD THESE TWO LINES — clear quote fields from previous PO
              setQuoteNo("");
              setQuoteDate("");

              // ✅ Auto-populate Created By / Created Date for a brand-new PO
              setPoCreatedBy(getCurrentUser());
              setPoCreatedDate(getTodayLocal());
              setPoApprovedBy("");
              setPoApprovedDate("");
              setPoProductType("");

              setModalData([
                {
                  purchase_organization: createPurchaseOrganization,
                  purchase_planner: createPurchasePlanner,
                  product_code: "",
                  description: "",
                  uom: "",
                  requirement_qty: "",
                  product_cost: "",
                  discount: 0,
                  status: "DRAFT"
                }
              ]);

              setEditableProducts([
                {
                  product_cost: "",
                  requirement_qty: "",
                  delivery_date: ""
                }
              ]);

              setShowModal(true);
              setIsEditMode(true);
            }}
          >
            + Create Purchase Order
          </button>
          )}

        </div>
      </div>

      <div style={styles.filterCard} className="po-filter-card">
        <div className="row g-4">

          {/* Purchase Organization */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Purchase Organization</label>
            <Select
              {...rsMenuProps}
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={purchaseOrganizations.map((item) => ({
                value: item.name || item.purchase_org || item,
                label: item.name || item.purchase_org || item,
              }))}
              value={selectedPurchaseOrganization
                ? { value: selectedPurchaseOrganization, label: selectedPurchaseOrganization }
                : null}
              onChange={(opt) => setSelectedPurchaseOrganization(opt?.value || "")}
            />
          </div>

          {/* Purchase Order ID */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Purchase Order ID</label>
            <Select
              {...rsMenuProps}
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={purchaseOrders.map((po) => ({
                value: po.purchase_order_id,
                label: po.purchase_order_id,
              }))}
              value={selectedPurchaseOrderId
                ? { value: selectedPurchaseOrderId, label: selectedPurchaseOrderId }
                : null}
              onChange={(opt) => setSelectedPurchaseOrderId(opt?.value || "")}
            />
          </div>

          {/* Vendor */}
          <div className="col-md-2 col-sm-6">
            <label style={styles.filterLabel}>Supplier</label>
            <Select
              {...rsMenuProps}
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={vendors.map((v) => ({ value: v.name, label: v.name }))}
              value={filterVendor ? { value: filterVendor, label: filterVendor } : null}
              onChange={(opt) => setFilterVendor(opt?.value || "")}
            />
          </div>
        </div>
      </div>


      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 8, padding: 3, gap: 2 }}>
          <button
            style={{ border: "none", borderRadius: 6, padding: "5px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              background: listTab === "active" ? "#fff" : "transparent",
              color: listTab === "active" ? "#1e293b" : "#64748b",
              boxShadow: listTab === "active" ? "0 1px 3px rgba(0,0,0,0.12)" : "none"
            }}
            onClick={() => setListTab("active")}
          >Active</button>
          <button
            style={{ border: "none", borderRadius: 6, padding: "5px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              background: listTab === "short_closed" ? "#fff" : "transparent",
              color: listTab === "short_closed" ? "#1e293b" : "#64748b",
              boxShadow: listTab === "short_closed" ? "0 1px 3px rgba(0,0,0,0.12)" : "none"
            }}
            onClick={() => setListTab("short_closed")}
          >Short Closed</button>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <table className="table" style={styles.erpTable}>
          <thead>
            <tr>
              <th style={{ ...styles.erpTh, width: 200 }}>
                PO No
              </th>

              <th style={{ ...styles.erpTh, width: 220 }}>
                Supplier
              </th>

              <th style={{ ...styles.erpTh, width: 200 }}>
                Purchase Organization
              </th>

              <th style={{ ...styles.erpTh, width: 150 }}>
              Total Value
              </th>
<th style={{ ...styles.erpTh, width: 130 }}>
  Creation Date
</th>
              <th style={{ ...styles.erpTh, width: 120 }}>
                Status
              </th>
              <th style={{ ...styles.erpTh, width: 130 }}>
                Acknowledgement
              </th>
              <th style={{ ...styles.erpTh, width: 100, textAlign: "center" }}>
              </th>
            </tr>
          </thead>

          <tbody>
            {poList.filter(row => {
              const ps = (row.po_status || row.status || "").toUpperCase();
              if (listTab === "short_closed") return ps === "SHORT_CLOSED";
              return ps !== "SHORT_CLOSED";
            }).length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  style={{
                    ...styles.erpTd,
                    textAlign: "center",
                    padding: 20
                  }}
                >
                  No records found
                </td>
              </tr>
            ) : (
              poList.filter(row => {
                const ps = (row.po_status || row.status || "").toUpperCase();
                if (listTab === "short_closed") return ps === "SHORT_CLOSED";
                return ps !== "SHORT_CLOSED";
              }).map((row, idx) => (
                <tr key={idx}>
                  <td style={{ ...styles.erpTd, fontWeight: 600 }}>
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        color: "#1d4ed8",
                        textDecoration: "underline",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                      onClick={() => viewPO(row.purchase_order_id)}
                    >
                      {row.purchase_order_id}
                    </button>
                  </td>

                  <td style={styles.erpTd}>
                    {row.vendor_name}
                  </td>

                  <td style={styles.erpTd}>
                    {row.purchase_organization}
                  </td>

                  <td style={styles.erpTd}>
                    ₹ {Number(row.final_price || 0).toLocaleString("en-IN")}
                  </td>
                  <td style={{ ...styles.erpTd, whiteSpace: "nowrap" }}>
  {formatDateDMY(row.created_at)}
</td>

                  <td style={styles.erpTd}>
                    {(() => {
                      const s = (row.status || "").trim();
                      const su = s.toUpperCase();
                      if (su === "CREATED") return "Created";
                      if (su === "APPROVED") return "Approved";
                      if (su === "REJECTED") return "Rejected";
                      if (s === "Partially Approved") return "Partially Approved";
                      if (s === "Partially Rejected") return "Partially Rejected";
                      if (su === "QUERY") return "Query";
                      return "Pending for Approval";
                    })()}
                  </td>
                  <td style={styles.erpTd}>
                    {String(row.acknowledgement_status || "").toUpperCase() === "ACKNOWLEDGED"
                      ? "Acknowledged"
                      : "Pending"}
                  </td>
                  <td style={{ ...styles.erpTd, textAlign: "center" }}>
                    <button
                      className="btn btn-outline-dark btn-sm py-0"
                      style={{ fontSize: "12px" }}
                      onClick={() => viewPO(row.purchase_order_id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>



      {showModal && modalData && (
        <div style={styles.modalBackdrop} className="po-modal-backdrop">
          <div
            style={{
              ...styles.modal,
              maxWidth: "1200px",
              width: "90%",
              maxHeight: "90vh",
              padding: "16px",
              fontSize: "14px"   // ⭐ GLOBAL COMPACT FONT (PR STYLE)
            }}
          >
            {/* ================= HEADER ================= */}
            <div style={{ ...styles.modalHeader, padding: "10px 14px" }}>
              <div style={{ fontWeight: 600, fontSize: "18px" }}>
                Purchase Order : {modalData?.[0]?.purchase_order_id || ""}
              </div>
              <button style={styles.iconBtn} onClick={closeModal}>✕</button>
            </div>

            {/* ================= BODY ================= */}
            <div style={{ ...styles.modalBody, padding: "14px" }} className="po-modal-body">

              {/* ===== ROW 1: Product Type / Order Type ===== */}
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Product Type</label>
                  {isFieldVisible("product_type") ? (
                    <Select
                      {...rsMenuProps}
                      styles={rsCompact}
                      options={productTypeOptions}
                      value={productTypeOptions.find(o => o.value === poProductType) || null}
                      onChange={(opt) => setPoProductType(opt?.value || "")}
                      isDisabled={!isEditMode}
                      placeholder="Select"
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* 🆕 Order Type — Domestic PO / Import PO */}
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Order Type</label>
                  <Select
                    {...rsMenuProps}
                    styles={rsCompact}
                    options={orderTypeOptions}
                    value={orderTypeOptions.find(o => o.value === poOrderType) || orderTypeOptions[0]}
                    onChange={(opt) => setPoOrderType(opt?.value || "domestic")}
                    isDisabled={!isEditMode}
                    placeholder="Select Order Type"
                  />
                </div>
              </div>

              {/* ===== ROW 2: Category / Sub Category / Created By / Approved By ===== */}
              <div className="row g-2 mb-3">
               
                {/* Created By */}
                <div className="col-md-2">
                  <label style={styles.compactLabel}>Created By</label>
                  {isFieldVisible("created_by") ? (
                    <input
                      className="form-control"
                      style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                      value={poCreatedBy}
                      readOnly
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Created Date */}
                <div className="col-md-2">
                  <label style={styles.compactLabel}>Created Date</label>
                  {isFieldVisible("created_date") ? (
                    <input
                      className="form-control"
                      style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                      value={formatDateDMY(poCreatedDate)}
                      readOnly
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Approved By */}
                <div className="col-md-2">
                  <label style={styles.compactLabel}>Approved By</label>
                  {isFieldVisible("approved_by") ? (
                    <input
                      className="form-control"
                      style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                      value={poApprovedBy}
                      readOnly
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Approved Date */}
                <div className="col-md-2">
                  <label style={styles.compactLabel}>Approved Date</label>
                  {isFieldVisible("approved_date") ? (
                    <input
                      className="form-control"
                      style={{ ...styles.compactInput, backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                      value={formatDateDMY(poApprovedDate)}
                      readOnly
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>
              </div>

              {/* ===== BASIC HEADER INFO ===== */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Purchase Organization</label>
                  {isFieldVisible("purchase_organization") ? (
                    <CreatableSelect
                      {...rsMenuProps}
                      styles={rsCompact}
                      menuShouldScrollIntoView={false}
                      isClearable
                      placeholder="Select or type Purchase Organization"
                      isDisabled={!isEditMode}
                      options={purchaseOrganizations.map((item) => ({
                        value: item.name || item,
                        label: item.name || item,
                      }))}
                      value={modalData?.[0]?.purchase_organization
                        ? { value: modalData?.[0].purchase_organization, label: modalData?.[0].purchase_organization }
                        : null}
                      onChange={(opt) => {
                        const newOrg = opt?.value || "";
                        const updated = modalData.map((row, i) =>
                          i === 0 ? { ...row, purchase_organization: newOrg } : row
                        );
                        setModalData(updated);
                        // 🆕 Purchase Organization is the other half of the
                        // (vendor, purchaseOrg) pair needed to resolve GST —
                        // if a Supplier was already picked before this was
                        // filled in, refetch now instead of leaving CGST/
                        // SGST/IGST stuck at 0 until the Supplier is re-picked.
                        refetchLineGst(selectedVendor, newOrg);
                      }}
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                <div className="col-md-4">
                  <label style={styles.compactLabel}>Purchase Planner</label>
                  {isFieldVisible("purchase_planner") ? (
                    <CreatableSelect
                      {...rsMenuProps}
                      styles={rsCompact}
                      menuShouldScrollIntoView={false}
                      isClearable
                      placeholder="Select or type Purchase Planner"
                      isDisabled={!isEditMode}
                      options={purchasePlanners.map((item) => ({
                        value: item.name || item,
                        label: item.name || item,
                      }))}
                      value={modalData?.[0]?.purchase_planner
                        ? { value: modalData?.[0].purchase_planner, label: modalData?.[0].purchase_planner }
                        : null}
                      onChange={(opt) => {
                        const updated = modalData.map((row, i) =>
                          i === 0 ? { ...row, purchase_planner: opt?.value || "" } : row
                        );
                        setModalData(updated);
                      }}
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                <div className="col-md-4">
                  <label style={styles.compactLabel}>Location</label>
                  {isFieldVisible("location") ? (
                    <CreatableSelect
                      {...rsMenuProps}
                      styles={rsCompact}
                      menuShouldScrollIntoView={false}
                      isClearable
                      placeholder="Select or type Location"
                      isDisabled={!isEditMode}
                      options={locationOptions.map((loc) => {
                        const v = typeof loc === "string" ? loc : (loc.name || loc.location || loc.value || "");
                        return { value: v, label: v };
                      })}
                      value={poLocation ? { value: poLocation, label: poLocation } : null}
                      onChange={(opt) => setPoLocation(opt?.value || "")}
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>
              </div>

              {/* ===== VENDOR ===== */}
              <div className="row g-3 mb-3 po-vendor-row">

                {/* Supplier */}
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Supplier</label>
                  {isFieldVisible("vendor") ? (
                    <FieldWithTooltip
                      tooltip={
                        selectedVendor
                          ? (vendors.find(v => v.id === selectedVendor)?.name || selectedVendor)
                          : ""
                      }
                    >
                      <CreatableSelect
                        {...rsMenuProps}
                        styles={rsCompact}
                        menuShouldScrollIntoView={false}
                        isClearable
                        placeholder="Select or type Supplier"
                        isDisabled={!isEditMode}
                       options={vendors.map((v) => ({
    value: v.id,
    label: `${v.name} - ${v.id}`
  }))}
                        value={
                          selectedVendor
                            ? {
                              value: selectedVendor,
                              label:
                                vendors.find(v => v.id === selectedVendor)?.name ||
                                selectedVendor
                            }
                            : null
                        }
                        onChange={(opt) =>
                          handleVendorSelect(opt?.value || "")
                        }
                      />
                    </FieldWithTooltip>
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Quote No */}
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Quote No</label>
                  {isFieldVisible("quote_no") ? (
                    <input
                      type="text"
                      className="form-control"
                      style={styles.compactInput}
                      value={quoteNo}
                      onChange={(e) => setQuoteNo(e.target.value)}
                      disabled={!isEditMode}
                      placeholder="Enter Quote No"
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

                {/* Quote Date */}
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Quote Date</label>
                  {isFieldVisible("quote_date") ? (
                    <DateInputDDMMYYYY
                      style={styles.compactInput}
                      value={quoteDate}
                      onChange={(val) => setQuoteDate(val)}
                      disabled={!isEditMode}
                    />
                  ) : (
                    <MaskedValue />
                  )}
                </div>

              </div>

              {/* Import Details header-level fields have been moved into the Customs tab (see LANDED_COST_TAB_IDS render block below) */}

              {/* ================= TABS ================= */}
              <div style={{ ...styles.tabs, marginBottom: "10px" }} className="po-tabs">
                {["basic", "product", ...LANDED_COST_TAB_IDS, "review", "confirmation",  "payment_history", "acknowledgement", "docs", "change_log" ] // 🆕 Acknowledgement tab, right after Payment History; 🆕 5 Landed Cost category tabs, right after Product (Import PO only)
                  .filter(tab => tab !== "review" || isPoSaved)
                  // 🆕 Not reviewable until Submit moves it out of "Created"
                  // (see submitPurchaseOrder) — hide the Review tab till then.
                  .filter(tab => tab !== "review" || (modalData?.[0]?.status || "").toUpperCase().trim() !== "CREATED")
                  // Landed Cost category tabs are only meaningful for Import POs.
                  .filter(tab => !LANDED_COST_TAB_IDS.includes(tab) || poOrderType === "import")
                  // Review-only role: only the Review tab is reachable — Basic,
                  // Product, Docs, Confirmation, Payment History, Acknowledgement, Change Log hidden.
                  .filter(tab => !isReviewOnly || tab === "review")
                  // Convert-only role: only the Confirmation tab (this module's
                  // convert action) is reachable.
                  .filter(tab => !isConvertOnly || tab === "confirmation")
                  .map(tab => (
                    <button
                      key={tab}
                      className={`btn ${createActiveTab === tab ? "btn-secondary" : "btn-light"}`}
                      style={styles.compactTabBtn}
                      onClick={() => setCreateActiveTab(tab)}
                    >
                      {tab === 'change_log'
                        ? 'Change Log'
                        : tab === 'payment_history'
                          ? 'Payment History'
                          : tab === 'acknowledgement'
                            ? 'Acknowledgement'
                            : LANDED_COST_TAB_IDS.includes(tab)
                              ? LANDED_COST_GROUPS.find(g => g.tabId === tab).group
                              : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
              </div>

              {/* ================= BASIC TAB ================= */}
              {createActiveTab === "basic" && (
                <div className="row g-3">

                  {/* Payment Terms */}
                  <div className="col-md-4">
                    <label style={styles.compactLabel}>
                      Payment Terms
                    </label>

                    {isFieldVisible("payment_terms") ? (
                      <div className="d-flex gap-2">

                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentTerms}
                          onChange={(e) => setPaymentTerms(e.target.value)}
                          disabled={!isEditMode}
                        />

                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() => setShowPaymentModal(true)}
                          disabled={!isEditMode}
                        >
                          View
                        </button>

                      </div>
                    ) : (
                      <MaskedValue />
                    )}
                  </div>

                  {/* Logistics fields — now shown for both Domestic PO and Import PO */}
                  {(
                    <>
                      {/* Mode Of Transport */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Mode Of Transport</label>
                        {isFieldVisible("mode_of_transport") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={modeOfTransportOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              modeOfTransport
                                ? { value: modeOfTransport, label: modeOfTransport }
                                : null
                            }
                            onChange={(opt) => setModeOfTransport(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>

                      {/* Delivery Type */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Delivery Type</label>
                        {isFieldVisible("delivery_type") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={deliveryTypeOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              deliveryType
                                ? { value: deliveryType, label: deliveryType }
                                : null
                            }
                            onChange={(opt) => setDeliveryType(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>

                      {/* Freight Terms */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Freight Terms</label>
                        {isFieldVisible("freight_terms") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={freightTermsOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              freightTerms
                                ? { value: freightTerms, label: freightTerms }
                                : null
                            }
                            onChange={(opt) => setFreightTerms(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>

                      {/* Insurance Terms */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Insurance Terms</label>
                        {isFieldVisible("insurance_terms") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={insuranceTermsOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              insuranceTerms
                                ? { value: insuranceTerms, label: insuranceTerms }
                                : null
                            }
                            onChange={(opt) => setInsuranceTerms(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>

                      {/* Transporter */}
                      <div className="col-md-4">
                        <label style={styles.compactLabel}>Transporter</label>
                        {isFieldVisible("transporter") ? (
                          <Select
                            {...rsMenuProps}
                            styles={rsCompact}
                            options={transporterOptions.map(x => ({
                              value: x.name,
                              label: x.name
                            }))}
                            value={
                              transporter
                                ? { value: transporter, label: transporter }
                                : null
                            }
                            onChange={(opt) => setTransporter(opt?.value || "")}
                            isDisabled={!isEditMode}
                          />
                        ) : (
                          <MaskedValue />
                        )}
                      </div>
                    </>
                  )}

                  {/* PO Header Lot Type */}
                  <div className="col-md-4">
                    <label style={styles.compactLabel}>PO Header Lot Type</label>
                    {isFieldVisible("po_header_lot_type") ? (
                      <Select
                        {...rsMenuProps}
                        styles={rsCompact}
                        options={poHeaderLotTypeOptions.map(x => ({
                          value: x.name,
                          label: x.name
                        }))}
                        value={
                          poHeaderLotType
                            ? { value: poHeaderLotType, label: poHeaderLotType }
                            : null
                        }
                        onChange={(opt) => setPoHeaderLotType(opt?.value || "")}
                        isDisabled={!isEditMode}
                      />
                    ) : (
                      <MaskedValue />
                    )}
                  </div>

                </div>
              )}

              {createActiveTab === "product" && (
                <div>

                  {isEditMode && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <button
                        className="btn btn-dark btn-sm"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={() => {
                          setModalData([
                            ...modalData,
                            {
                              product_code: "",
                              description: "",
                              uom: "",
                              requirement_qty: "",
                              product_cost: "",
                              discount: 0,
                              status: "PENDING"
                            }
                          ]);
                          setEditableProducts([
                            ...editableProducts,
                            {
                              product_cost: "",
                              requirement_qty: "",
                              delivery_date: ""
                            }
                          ]);
                        }}
                      >
                        + Add Product
                      </button>
                    </div>
                  )}

                  {isEditMode && String(poProductType).trim().toLowerCase() !== 'asset' && (
                    <div
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '10px 12px',
                        marginBottom: 10
                      }}
                    >

                      {/* Filter Row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                       

                        {/* Category */}
                        <div style={{ minWidth: 160, flex: '1 1 150px', maxWidth: 220 }}>
                          <Select
                            {...rsMenuProps}
                            isClearable
                            isDisabled={!isEditMode}
                            placeholder="All Categories"
                            menuShouldScrollIntoView={false}
                            options={categoryOptions.map(c => ({
                              value: c.name || c,
                              label: c.name || c
                            }))}
                            value={
                              filterCategory
                                ? { value: filterCategory, label: filterCategory }
                                : null
                            }
                            onChange={(opt) => {
                              setFilterCategory(opt?.value || "");
                              setFilterSubCategory("");
                            }}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: 30,
                                fontSize: '12px'
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: '2px 6px'
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                                fontSize: '12px'
                              }),
                              indicatorsContainer: (base) => ({
                                ...base,
                                alignSelf: 'flex-start',
                                paddingTop: 2
                              }),
                              dropdownIndicator: (base) => ({
                                ...base,
                                padding: 2
                              }),
                              clearIndicator: (base) => ({
                                ...base,
                                padding: 2
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontSize: '12px',
                                color: '#94a3b8',
                                fontWeight: 400
                              }),
                              option: (base, state) => ({
                                ...base,
                                fontSize: '12px',
                                fontWeight: 400,
                                color: state.isSelected ? '#fff' : '#334155',
                                backgroundColor: state.isSelected
                                  ? '#475569'
                                  : state.isFocused
                                    ? '#f1f5f9'
                                    : '#fff',
                                padding: '6px 10px'
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999
                              }),
                              menu: (base) => ({
                                ...base,
                                fontSize: '12px'
                              })
                            }}
                          />
                        </div>

                        {/* Sub Category */}
                        <div style={{ minWidth: 160, flex: '1 1 150px', maxWidth: 220 }}>
                          <Select
                            {...rsMenuProps}
                            isClearable
                            isDisabled={!isEditMode}
                            placeholder="All Sub-Categories"
                            menuShouldScrollIntoView={false}
                            options={subCategoryOptions
                              .filter(sc => {
                                const scName = sc.name || sc;
                                if (!filterCategory) return true;
                                return allProducts.some(
                                  p =>
                                    p.productCategory === filterCategory &&
                                    p.subCategory === scName
                                );
                              })
                              .map(sc => ({
                                value: sc.name || sc,
                                label: sc.name || sc
                              }))}
                            value={
                              filterSubCategory
                                ? { value: filterSubCategory, label: filterSubCategory }
                                : null
                            }
                            onChange={(opt) => setFilterSubCategory(opt?.value || "")}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: 30,
                                fontSize: '12px'
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: '2px 6px'
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                                fontSize: '12px'
                              }),
                              indicatorsContainer: (base) => ({
                                ...base,
                                alignSelf: 'flex-start',
                                paddingTop: 2
                              }),
                              dropdownIndicator: (base) => ({
                                ...base,
                                padding: 2
                              }),
                              clearIndicator: (base) => ({
                                ...base,
                                padding: 2
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontSize: '12px',
                                color: '#94a3b8',
                                fontWeight: 400
                              }),
                              option: (base, state) => ({
                                ...base,
                                fontSize: '12px',
                                fontWeight: 400,
                                color: state.isSelected ? '#fff' : '#334155',
                                backgroundColor: state.isSelected
                                  ? '#475569'
                                  : state.isFocused
                                    ? '#f1f5f9'
                                    : '#fff',
                                padding: '6px 10px'
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999
                              }),
                              menu: (base) => ({
                                ...base,
                                fontSize: '12px'
                              })
                            }}
                          />
                        </div>

                        {isEditMode && (filterCategory || filterSubCategory) && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{
                              fontSize: 11,
                              whiteSpace: 'nowrap',
                              height: 30,
                              padding: '0 10px'
                            }}
                            onClick={() => {
                              setFilterCategory("");
                              setFilterSubCategory("");
                            }}
                          >
                            ✕ Clear
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                    <table className="table" style={{ ...styles.erpTable, tableLayout: "auto" }}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.erpTh, width: '40px', textAlign: 'center' }}>Item</th>
                          <th style={{ ...styles.erpTh, width: '200px' }}>
                            {String(poProductType).trim().toLowerCase() === "asset" ? "Asset Code" : "Product Code"}
                          </th>
                          <th style={{ ...styles.erpTh, width: '100px', textAlign: 'center' }}>Product Class</th>
                          <th style={{ ...styles.erpTh, minWidth: '160px' }}>
                            {String(poProductType).trim().toLowerCase() === "asset" ? "Asset Description" : "Description"}
                          </th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Qty</th>
                          <th style={{ ...styles.erpTh, width: '100px', textAlign: 'center' }}>Purchase Class</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '130px' }}>Range</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '60px' }}>UOM</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '80px' }}>Currency</th>
<th style={{ ...styles.erpTh, textAlign: 'center', width: '120px' }}>HSN Code</th>
<th style={{ ...styles.erpTh, textAlign: 'right', width: '90px' }}>Unit Cost</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '70px' }}>Disc (%)</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Total Cost</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '100px' }}>Net Price</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col1 || "—"}</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col2 || "—"}</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '80px' }}>{gstColumnLabels.col3 || "—"}</th>
                          <th style={{ ...styles.erpTh, textAlign: 'right', width: '110px' }}>Grand Total</th>
                          <th style={{ ...styles.erpTh, textAlign: 'center', width: '110px', whiteSpace: 'nowrap' }}>Delivery Date</th>
                          {poOrderType === "import" && (
                            <>
                              <th style={{ ...styles.erpTh, textAlign: 'center', width: '120px' }}>Country Of Origin</th>
                            </>
                          )}
                          <th style={{ ...styles.erpTh, width: '60px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalData.map((row, idx) => {
                          const calc = calculateLineTotals(row, idx);

                          return (
                            <tr key={idx}>
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>{idx + 1}</td>

                              {/* Product Code — dropdown showing "CODE - Description" */}
                              <td style={styles.erpTd}>
                                {!isFieldVisible("product_code") ? (
                                  <MaskedValue height={24} />
                                ) : (
                                <Select
                                  {...rsMenuProps}
                                  styles={{
                                    ...rsCompact,
                                    container: (base) => ({ ...base, minWidth: "180px" }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  }}
                                  menuShouldScrollIntoView={false}
                                  placeholder={String(poProductType).trim().toLowerCase() === "asset" ? "Select Asset" : "Select Product"}
                                  isDisabled={!isEditMode}
                                  value={row.product_code
                                    ? {
                                      value: row.product_code,
                                      label: (() => {
                                        const p = allProducts.find(p => p.productCode === row.product_code);
                                        return p
                                          ? `${p.productCode} - ${p.description || p.productDescription || ""}`
                                          : row.product_code;
                                      })()
                                    }
                                    : null}
                                  options={allProducts
                                    .filter(p => {
                                      if (
                                        poProductType &&
                                        String(p.productType || "").trim().toLowerCase() !==
                                          String(poProductType).trim().toLowerCase()
                                      ) return false;
                                      if (filterCategory && p.productCategory !== filterCategory) return false;
                                      if (filterSubCategory && p.subCategory !== filterSubCategory) return false;
                                      return true;
                                    })
                                    .map((p) => ({
                                      value: p.productCode,
                                      label: `${p.productCode} - ${p.description || p.productDescription || ""}`,
                                    }))}
                                  onChange={async (opt) => {
                                    const code = opt?.value || "";
                                    const product = allProducts.find(p => p.productCode === code);

                                    // ✅ Check if this is an existing saved line BEFORE resetting
                                    const isExistingLine = !!modalData[idx]?.id;

                                    const updated = [...modalData];
                                    updated[idx] = {
                                      ...updated[idx],
                                      product_code: product?.productCode || "",
                                      description:
                                        product?.description ||
                                        product?.productDescription ||
                                        product?.product_description || "",
                                      uom: product?.uom || "",
                                      // 🆕 currency/product_cost below are overwritten by
                                      // applyExchangeRateToLineItems() right after setModalData
                                      // (a few lines down) whenever we're on an Import PO with
                                      // a Currency + Exchange Rate already set — base_cost_inr
                                      // is what that conversion is always computed FROM, so it
                                      // must be captured here from the (INR) product master,
                                      // never from an already-converted value.
                                      currency: product?.currency || "",
                                      base_cost_inr: product?.productCost || 0,
                                      product_cost: product?.productCost || 0,
                                      requirement_qty: "",
                                      delivery_date: "",
                                      // ✅ Tax % now comes from the HSN code
                                      // (hsn_code_data table) instead of being
                                      // hardcoded, so CGST/SGST/IGST reflect the
                                      // product's actual HSN code, not qty.
                                      cgst_percent: Number(product?.cgstPercent ?? 0),
                                      sgst_percent: Number(product?.sgstPercent ?? 0),
                                      igst_percent: Number(product?.igstPercent ?? 0),
                                      hsn_code: product?.hsnCode || "",
                                      product_class: product?.productClass || "",
                                      purchase_class: product?.purchaseClass || "",
                                      // ✅ Keep existing characteristics for saved lines, clear only for new lines
                                      characteristics: isExistingLine
                                        ? (modalData[idx].characteristics || [])
                                        : [],
                                    };
                                    setModalData(updated);

                                    // 🆕 If this is an Import PO and we already have a
                                    // Currency + Exchange Rate on the Customs tab, convert
                                    // this newly-added row's cost immediately instead of
                                    // leaving it in INR until the next country change.
                                    if (poOrderType === "import" && importCurrency && importExchangeRate) {
                                      applyExchangeRateToLineItems(importCurrency, Number(importExchangeRate));
                                    }

                                    // 🆕 Clear any stale calculatedAmounts for this row —
                                    // otherwise calculateLineTotals() keeps using the OLD
                                    // GST% (from a previous "View" click) instead of the
                                    // fresh cgst_percent/sgst_percent/igst_percent we just
                                    // set above, so CGST/SGST/IGST in the table wouldn't
                                    // update until the user clicked View again.
                                    setCalculatedAmounts(prev => {
                                      const next = { ...prev };
                                      delete next[idx];
                                      return next;
                                    });

                                    // 🆕 Resolve real GST for THIS row immediately, instead of
                                    // waiting for the user to click "View" or re-pick the
                                    // vendor. Previously the row above only got
                                    // cgst_percent/sgst_percent/igst_percent from the product
                                    // master (stale/0 now that GST comes from
                                    // gst_configuration), so any row the user didn't manually
                                    // "View" saved with 0% GST. If a vendor is already known,
                                    // fetch this one product's real GST now and stash it in
                                    // calculatedAmounts — the same place Save() and the table
                                    // read GST from — so it's correct the moment a product is
                                    // picked, no extra click required. Purchase Organization is
                                    // NOT required here — gst_configuration is looked up by
                                    // movement_type + hsn_code only.
                                    const orgForGst = modalData?.[0]?.purchase_organization || "";
                                    // 🆕 Fallback source: the product master's own
                                    // cgstPercent/sgstPercent/igstPercent (same fields
                                    // View's "!lineItemId" branch already uses locally).
                                    // If gst_configuration has no matching HSN+movement
                                    // row yet (or billing_state is missing on the
                                    // supplier), get-product-gst legitimately/silently
                                    // comes back all-zero — previously that zero got
                                    // written into calculatedAmounts as-is, so the table
                                    // showed 0.00 until "View" was opened and recomputed
                                    // from this same fallback field. Applying the same
                                    // fallback here keeps both paths in sync from the
                                    // start, no extra click required.
                                    const fallbackGst = {
                                      cgst: Number(product?.cgstPercent ?? 0),
                                      sgst: Number(product?.sgstPercent ?? 0),
                                      igst: Number(product?.igstPercent ?? 0),
                                    };
                                    const isImportRow = poOrderType === "import";
                                    // Import POs resolve GST off Supplier Country, not the
                                    // vendor — selectedVendor is irrelevant to the Import
                                    // gst_configuration lookup (see getGstForPO on the backend).
                                    const canResolveGst = isImportRow ? !!importSupplierCountry : !!selectedVendor;
                                    if (code && canResolveGst) {
                                      try {
                                        const gstRes = await axios.post(
                                          `${IMPORT_PO_API}/get-product-gst`,
                                          {
                                            productCodes: [code],
                                            vendorCode: selectedVendor,
                                            purchaseOrganization: orgForGst,
                                            orderType: poOrderType,
                                            supplierCountry: isImportRow ? importSupplierCountry : undefined
                                          },
                                          { headers: authHeaders() }
                                        );
                                        const g = gstRes.data?.[code];
                                        const isAllZero =
                                          !g ||
                                          ((Number(g.cgst) || 0) === 0 &&
                                            (Number(g.sgst) || 0) === 0 &&
                                            (Number(g.igst) || 0) === 0);

                                        // Import: positional mapping from the real tax
                                        // breakdown (taxes[]) into the 3 slots, and the
                                        // column headers get relabeled to match — no
                                        // name-matching against "CGST"/"SGST"/"IGST"
                                        // since Import taxes aren't necessarily named that.
                                        const resolved = isImportRow
                                          ? {
                                              cgst: Number(g?.taxes?.[0]?.percent ?? 0),
                                              sgst: Number(g?.taxes?.[1]?.percent ?? 0),
                                              igst: Number(g?.taxes?.[2]?.percent ?? 0),
                                            }
                                          : (isAllZero ? fallbackGst : { cgst: g.cgst, sgst: g.sgst, igst: g.igst });

                                        if (isImportRow) {
                                          setGstColumnLabels(
                                            g?.taxes?.length
                                              ? {
                                                  col1: g.taxes[0]?.type || DEFAULT_GST_COLUMN_LABELS.col1,
                                                  col2: g.taxes[1]?.type || "",
                                                  col3: g.taxes[2]?.type || "",
                                                }
                                              : DEFAULT_GST_COLUMN_LABELS
                                          );
                                        }

                                        if (isAllZero && !isImportRow) {
                                          console.warn(
                                            `GST config lookup returned 0% for product ${code} — falling back to product master cgst/sgst/igst. Check gst_configuration / supplier billing_state.`
                                          );
                                        }

                                        setCalculatedAmounts(prev => ({
                                          ...prev,
                                          [idx]: {
                                            ...prev[idx],
                                            cgst: resolved.cgst,
                                            sgst: resolved.sgst,
                                            igst: resolved.igst,
                                            hsnCode: g?.hsnCode || product?.hsnCode || ""
                                          }
                                        }));
                                      } catch (gstErr) {
                                        console.error("Error resolving GST for new row, using product master fallback:", gstErr);
                                        setCalculatedAmounts(prev => ({
                                          ...prev,
                                          [idx]: {
                                            ...prev[idx],
                                            cgst: fallbackGst.cgst,
                                            sgst: fallbackGst.sgst,
                                            igst: fallbackGst.igst,
                                            hsnCode: product?.hsnCode || ""
                                          }
                                        }));
                                      }
                                    } else {
                                      // No vendor (Domestic) / no Supplier Country (Import)
                                      // yet — still seed the table with the product
                                      // master's rate instead of 0, so it's not
                                      // misleadingly blank before that's picked.
                                      setCalculatedAmounts(prev => ({
                                        ...prev,
                                        [idx]: {
                                          ...prev[idx],
                                          cgst: fallbackGst.cgst,
                                          sgst: fallbackGst.sgst,
                                          igst: fallbackGst.igst,
                                          hsnCode: product?.hsnCode || ""
                                        }
                                      }));
                                    }

                                    // ✅ Auto-populate header Product Type from the
                                    // selected product, if it hasn't been set yet
                                    if (product?.productType && !poProductType) {
                                      setPoProductType(product.productType);
                                    }

                                    const updatedEditable = [...editableProducts];
                                    const masterCostInr = Number(product?.productCost || 0);
                                    const activeRate = Number(importExchangeRate) || 0;
                                    const shouldConvertNow = poOrderType === "import" && importCurrency && activeRate;
                                    updatedEditable[idx] = {
                                      ...updatedEditable[idx],
                                      base_cost_inr: masterCostInr, // 🆕 always the raw INR figure
                                      product_cost: shouldConvertNow ? masterCostInr * activeRate : masterCostInr,
                                      requirement_qty: "",
                                      delivery_date: "",
                                    };
                                    setEditableProducts(updatedEditable);

                                    /* ── PIR auto-populate — SKIP for existing saved lines ── */
                                    if (code && !isExistingLine) {   // ✅ Only fetch PIR for NEW lines
                                      try {
                                        const pirRes = await axios.get(`${IMPORT_PO_API}/pir-info/${code}`, { headers: authHeaders() });
                                        const pir = pirRes.data;

                                        if (pir && pir.found) {
                                          // Only override with the PIR's cost when it's an actual
                                          // positive price. If the vendor's PIR has no price set
                                          // (0, null, blank), keep the cost already pulled from
                                          // Product Master instead of blanking it out.
                                          const pirCostNum = Number(pir.productCost);
                                          // 🆕 PIR cost is raw INR from the backend, same as
                                          // the product-master seed above — convert it the
                                          // same way if a foreign Currency + Exchange Rate is
                                          // already active, instead of letting this overwrite
                                          // the converted value with a raw INR number.
                                          const pirRate = Number(importExchangeRate) || 0;
                                          const pirShouldConvert = poOrderType === "import" && importCurrency && pirRate;
                                          const pirCostForDisplay =
                                            pirCostNum > 0
                                              ? (pirShouldConvert ? pirCostNum * pirRate : pirCostNum)
                                              : null; // null = "no override, keep existing"

                                          setModalData(prev => {
                                            const next = [...prev];
                                            next[idx] = {
                                              ...next[idx],
                                              base_cost_inr: pirCostNum > 0 ? pirCostNum : next[idx].base_cost_inr,
                                              product_cost: pirCostForDisplay !== null ? pirCostForDisplay : next[idx].product_cost,
                                              product_class: pir.productClass || next[idx].product_class,
                                              purchase_class: pir.purchaseClass || next[idx].purchase_class,
                                              characteristics: pir.characteristics || [],
                                              _pirVendorName: pir.vendorName || "",
                                              pir_number: pir.pir_number || null,
                                            };
                                            return next;
                                          });

                                          setEditableProducts(prev => {
                                            const next = [...prev];
                                            next[idx] = {
                                              ...next[idx],
                                              base_cost_inr: pirCostNum > 0 ? pirCostNum : next[idx].base_cost_inr,
                                              product_cost: pirCostForDisplay !== null ? pirCostForDisplay : next[idx].product_cost,
                                            };
                                            return next;
                                          });

                                          /* Vendor conflict check */
                                          const otherVendorNames = modalData
                                            .filter((_, i) => i !== idx && _?.product_code)
                                            .map(r => r._pirVendorName)
                                            .filter(Boolean);

                                          const hasConflict =
                                            otherVendorNames.length > 0 &&
                                            otherVendorNames.some(v => v !== pir.vendorName);

                                          if (hasConflict) {
                                            toast.warning(
                                              `Warning: Products in this PO have different default vendors. ` +
                                              `"${code}" defaults to "${pir.vendorName}" which differs from other products.`
                                            );
                                          } else {
                                            if (pir.vendorName && !selectedVendor) {
                                              await handleVendorSelect(pir.vendorCode || pir.vendorName);
                                            }
                                            if (pir.paymentTerms && !paymentTermNumber) {
                                              setPaymentTermNumber(pir.paymentTerms);
                                            }
                                          }
                                        } else if (pir && pir.characteristics) {
                                          // ✅ No PIR exists for this product, but the backend
                                          // still returns blank characteristic fields based on
                                          // the product's class — use those instead of leaving
                                          // the row with an empty characteristics array.
                                          setModalData(prev => {
                                            const next = [...prev];
                                            next[idx] = {
                                              ...next[idx],
                                              characteristics: pir.characteristics || [],
                                            };
                                            return next;
                                          });
                                        }
                                      } catch (pirErr) {
                                        console.error("PIR info fetch failed:", pirErr);
                                      }
                                    }
                                  }}
                                />
                                )}
                              </td>

                              {/* Class — from productdetails.productClass */}
                              <td style={{ ...styles.erpTd, textAlign: 'center', fontSize: '12px' }}>
                                {!isFieldVisible("product_class") ? <MaskedValue height={20} /> : <>
                                {row.product_class || (allProducts.find(p => p.productCode === row.product_code)?.productClass) || ""}
                                {(row.product_class || allProducts.find(p => p.productCode === row.product_code)?.productClass) && (
                                  <span
                                    style={{ cursor: 'pointer', marginLeft: 6, color: '#1d4ed8', fontWeight: 'bold', fontSize: '11px' }}
                                    title="View / Edit Characteristics"
                                    onClick={() => handleOpenCharModal(idx, "product")}
                                  >
                                    ▶
                                  </span>
                                )}
                                </>}
                              </td>

                              {/* Description — full text, wrapping */}
                              <td
                                style={{
                                  ...styles.erpTd,
                                  minWidth: '160px',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.4'
                                }}
                              >
                                {isFieldVisible("description")
                                  ? (row.description || row.product_description)
                                  : <MaskedValue height={20} />}
                              </td>

                              {/* Qty */}
                              <td style={styles.erpTd}>
                                {isFieldVisible("requirement_qty") ? (
                                  <input
                                    className="form-control form-control-sm p-1 text-end"
                                    style={{ fontSize: '12px', minWidth: '80px' }}
                                    value={editableProducts[idx]?.requirement_qty || ""}
                                    onChange={(e) => handleProductFieldChange(idx, "requirement_qty", e.target.value)}
                                    disabled={!isEditMode}
                                  />
                                ) : (
                                  <MaskedValue height={24} />
                                )}
                              </td>

                              {/* Purchase Class — from productdetails.purchaseClass */}
                              <td style={{ ...styles.erpTd, textAlign: 'center', fontSize: '12px' }}>
                                {!isFieldVisible("purchase_class") ? <MaskedValue height={20} /> : <>
                                {row.purchase_class || (allProducts.find(p => p.productCode === row.product_code)?.purchaseClass) || ""}
                                {(row.purchase_class || allProducts.find(p => p.productCode === row.product_code)?.purchaseClass) && (
                                  <span
                                    style={{ cursor: 'pointer', marginLeft: 6, color: '#1d4ed8', fontWeight: 'bold', fontSize: '11px' }}
                                    title="View / Edit Characteristics"
                                    onClick={() => handleOpenCharModal(idx, "purchase")}
                                  >
                                    ▶
                                  </span>
                                )}
                                </>}
                              </td>

                              {/* Range — derived from Qty ± Under/Over Tolerance % (set via Characteristics modal) */}
                              <td style={{ ...styles.erpTd, textAlign: 'center', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {!isFieldVisible("range") ? <MaskedValue height={20} /> : (
                                  getToleranceRange(row, idx).rangeText ? (
                                    <span style={{ color: '#16a34a', fontWeight: 600 }}>
                                      {getToleranceRange(row, idx).rangeText}
                                    </span>
                                  ) : (
                                    "—"
                                  )
                                )}
                              </td>

                              {/* UOM */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {isFieldVisible("uom") ? row.uom : <MaskedValue height={20} />}
                              </td>

                              {/* Currency */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {isFieldVisible("currency") ? (row.currency || "") : <MaskedValue height={20} />}
                              </td>

                              {/* HSN Code */}
<td style={{ ...styles.erpTd, textAlign: 'center' }}>
  {isFieldVisible("hsn_code") ? (
    row.hsn_code ||
    row.hsnCode ||
    allProducts.find(
      p => p.productCode === row.product_code
    )?.hsnCode ||
    ""
  ) : (
    <MaskedValue height={20} />
  )}
</td>

                              {/* Unit Cost */}
                              <td style={styles.erpTd}>
                                {isFieldVisible("product_cost") ? (
                                  <input
                                    className="form-control form-control-sm p-1 text-end"
                                    style={{ fontSize: '12px' }}
                                    value={editableProducts[idx]?.product_cost || ""}
                                    onChange={(e) => handleProductFieldChange(idx, "product_cost", e.target.value)}
                                    onBlur={() => handleProductCostBlur(idx)}
                                    disabled={!isEditMode}
                                  />
                                ) : (
                                  <MaskedValue height={24} />
                                )}
                              </td>

                              {/* Disc (%) */}
                              <td style={styles.erpTd}>
                                {isFieldVisible("discount") ? (
                                  <input
                                    className="form-control form-control-sm p-1 text-end"
                                    style={{ fontSize: '12px' }}
                                    value={discounts[idx] || ""}
                                    onChange={(e) => handleDiscountChange(e, idx)}
                                    disabled={!isEditMode}
                                  />
                                ) : (
                                  <MaskedValue height={24} />
                                )}
                              </td>

                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("total_cost") ? formatCurrency(calc.grossTotal) : <MaskedValue height={20} />}</td>
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("net_price") ? formatCurrency(calc.netPrice) : <MaskedValue height={20} />}</td>
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("cgst_percent") ? formatCurrency(calc.cgstAmt) : <MaskedValue height={20} />}</td>
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("sgst_percent") ? formatCurrency(calc.sgstAmt) : <MaskedValue height={20} />}</td>
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>
  {isFieldVisible("igst_percent") ? formatCurrency(calc.igstAmt) : <MaskedValue height={20} />}
</td>
                              <td style={{ ...styles.erpTd, textAlign: 'right' }}>{isFieldVisible("grand_total") ? formatCurrency(calc.finalPrice) : <MaskedValue height={20} />}</td>

                              {/* Requirement Date */}
                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                {!isFieldVisible("delivery_date") ? (
                                  <MaskedValue height={20} />
                                ) : isEditMode ? (
                                  <DateInputDDMMYYYY
                                    className="form-control form-control-sm p-1"
                                    style={{ fontSize: '12px', minWidth: '120px' }}
                                    value={editableProducts[idx]?.delivery_date || ""}
                                    onChange={(val) => {
                                      // Update only this row's date in editableProducts — don't touch modalData
                                      setEditableProducts(prev => {
                                        const next = [...prev];
                                        next[idx] = { ...next[idx], delivery_date: val };
                                        return next;
                                      });
                                    }}
                                  />
                                ) : (
                                  <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                    {formatDateDMY(editableProducts[idx]?.delivery_date || row.delivery_date) || "—"}
                                  </span>
                                )}
                              </td>

                              {/* 🆕 Import PO — item-level fields */}
                              {poOrderType === "import" && (
                                <>
                                  <td style={styles.erpTd}>
                                    {/* Country Of Origin always mirrors the header's
                                        Supplier Country — not independently editable
                                        per line, so it can never drift out of sync. */}
                                    <span style={{ fontSize: '12px' }}>
                                      {importSupplierCountry || "—"}
                                    </span>
                                  </td>
                                </>
                              )}

                              <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                                  <button
                                    className="btn btn-outline-dark btn-sm py-0"
                                    style={{ fontSize: '11px' }}
                                    onClick={() => handleViewProductDetails(row.id, idx)}
                                  >
                                    View
                                  </button>
                                  {isEditMode && (
                                    <button
                                      className="btn btn-sm py-0"
                                      style={{ color: '#ef4444', border: 'none', background: 'transparent', lineHeight: 1, padding: '2px 6px' }}
                                      title="Remove row"
                                      onClick={() => {
                                        // Track DB id so backend can delete it
                                        if (row.id) {
                                          setDeletedLineItemIds(prev => [...prev, row.id]);
                                        }
                                        const updatedModal = modalData.filter((_, i) => i !== idx);
                                        const updatedEditable = editableProducts.filter((_, i) => i !== idx);
                                        const updatedDiscounts = {};
                                        Object.keys(discounts).forEach(k => {
                                          const ki = parseInt(k);
                                          if (ki < idx) updatedDiscounts[ki] = discounts[ki];
                                          else if (ki > idx) updatedDiscounts[ki - 1] = discounts[ki];
                                        });
                                        setModalData(updatedModal);
                                        setEditableProducts(updatedEditable);
                                        setDiscounts(updatedDiscounts);
                                      }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}

                        <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>
                          <td colSpan={12} style={{ ...styles.erpTd, textAlign: 'right' }}>TOTALS:</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.gross)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.net)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.cgst)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.sgst)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.igst)}</td>
                          <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px', whiteSpace: 'nowrap' }}>₹ {formatCurrency(totals.grand)}</td>
                          <td style={styles.erpTd}></td>
                          <td style={styles.erpTd}></td>
                        </tr>

                        {/* 🆕 Port/Clearing total and Bank total — shown as two
                            separate rows under the Grand Total column, each PO-level
                            (single value, not per line item). Only meaningful for
                            Import POs, since both those tabs only render when
                            poOrderType === "import" (see LANDED_COST_TAB_IDS filter
                            above). */}
                        {poOrderType === "import" && (
                          <>
                            <tr style={{ backgroundColor: '#eef2ff', fontWeight: 'bold' }}>
                              <td colSpan={12} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                Port/Clearing Total:
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                ₹ {formatCurrency(portClearingTotal)}
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                            </tr>
                            <tr style={{ backgroundColor: '#eef2ff', fontWeight: 'bold' }}>
                              <td colSpan={12} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                Bank Total:
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                ₹ {formatCurrency(bankTotal)}
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                            </tr>
                            <tr style={{ backgroundColor: '#dcfce7', fontWeight: 'bold' }}>
                              <td colSpan={12} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                Final Total:
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                              <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                ₹ {formatCurrency((totals.grand || 0) + (portClearingTotal || 0) + (bankTotal || 0))}
                              </td>
                              <td style={styles.erpTd}></td>
                              <td style={styles.erpTd}></td>
                            </tr>
                          </>
                        )}


                      </tbody>
                    </table>


                  </div>
                </div>
              )}

              {/* ================= LANDED COST CATEGORY TABS (Import PO only) ================= */}
              {/* Customs renders one row per product line item (with Product Code).
                  Port/Clearing and Bank are PO-level — a single row, no Product Code
                  column, not repeated per line item. */}
              {LANDED_COST_TAB_IDS.includes(createActiveTab) && (() => {
                const activeGroup = LANDED_COST_GROUPS.find(g => g.tabId === createActiveTab);

                // PO-level groups (Customs, Port/Clearing, Bank) — single row,
                // keyed under a fixed pseudo-key. No Product Code column,
                // not repeated per product line item.
                {
                  const rowKey = "po_level";
                  const entry = landedCosts[rowKey] || {};
                  const groupSubtotal = activeGroup.fields
                    .reduce((sum, f) => sum + (Number(entry[f.key]) || 0), 0);

                  return (
                    <div>
                      {/* 🆕 Import details — only on the Customs tab, same fields/order
                          as before, rendered above the manual landed-cost table. */}
                      {createActiveTab === "landed_customs" && (
                        <>
                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Supplier Country</label>
                              <Select
                                {...rsMenuProps}
                                styles={rsCompact}
                                placeholder="Select Supplier Country"
                                options={countryOptions.map(c => ({ value: c.name || c, label: c.name || c }))}
                                value={importSupplierCountry ? { value: importSupplierCountry, label: importSupplierCountry } : null}
                                onChange={(opt) => {
                                  const val = opt?.value || "";
                                  setImportSupplierCountry(val);
                                  // 🆕 Import POs resolve GST off Supplier Country (not
                                  // vendor) — refetch line-item GST/labels the moment a
                                  // country is picked, same as vendor-change does for
                                  // Domestic POs, instead of waiting for a manual "View".
                                  if (poOrderType === "import") {
                                    refetchLineGstForCountry(val);
                                    // 🆕 Auto-fill Currency + Exchange Rate from Exchange
                                    // Rate Configuration, then push the converted Unit Cost
                                    // + Currency into every Product-tab line item.
                                    fetchExchangeRateForCountry(val);
                                  }
                                }}
                                isDisabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Currency</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importCurrency}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setImportCurrency(val);
                                  // 🆕 Manual override — re-apply so Product tab currency
                                  // stays in sync with whatever's typed here.
                                  applyExchangeRateToLineItems(val, Number(importExchangeRate) || 0);
                                }}
                                disabled={!isEditMode}
                                placeholder="e.g. USD"
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Exchange Rate</label>
                              <input
                                type="number"
                                className="form-control"
                                style={styles.compactInput}
                                value={importExchangeRate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setImportExchangeRate(val);
                                  // 🆕 Manual override — recompute every line item's Unit
                                  // Cost against the new rate immediately.
                                  applyExchangeRateToLineItems(importCurrency, Number(val) || 0);
                                }}
                                disabled={!isEditMode}
                                placeholder="e.g. 83.25"
                              />
                            </div>
                          </div>

                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Incoterm</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importIncoterm}
                                onChange={(e) => setImportIncoterm(e.target.value)}
                                disabled={!isEditMode}
                                placeholder="e.g. FOB, CIF, EXW"
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Port Of Loading</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importPortOfLoading}
                                onChange={(e) => setImportPortOfLoading(e.target.value)}
                                disabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Port Of Discharge</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importPortOfDischarge}
                                onChange={(e) => setImportPortOfDischarge(e.target.value)}
                                disabled={!isEditMode}
                              />
                            </div>
                          </div>

                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Final Destination</label>
                              <input
                                type="text"
                                className="form-control"
                                style={styles.compactInput}
                                value={importFinalDestination}
                                onChange={(e) => setImportFinalDestination(e.target.value)}
                                disabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Expected Shipment Date</label>
                              <DateInputDDMMYYYY
                                style={styles.compactInput}
                                value={importExpectedShipmentDate}
                                onChange={(val) => setImportExpectedShipmentDate(val)}
                                disabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Expected Arrival Date</label>
                              <DateInputDDMMYYYY
                                style={styles.compactInput}
                                value={importExpectedArrivalDate}
                                onChange={(val) => setImportExpectedArrivalDate(val)}
                                disabled={!isEditMode}
                              />
                            </div>
                          </div>

                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label style={styles.compactLabel}>LC Required</label>
                              <Select
                                {...rsMenuProps}
                                styles={rsCompact}
                                options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                value={{ value: importLcRequired, label: importLcRequired }}
                                onChange={(opt) => setImportLcRequired(opt?.value || "No")}
                                isDisabled={!isEditMode}
                              />
                            </div>

                            <div className="col-md-4">
                              <label style={styles.compactLabel}>Insurance Required</label>
                              <Select
                                {...rsMenuProps}
                                styles={rsCompact}
                                options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                value={{ value: importInsuranceRequired, label: importInsuranceRequired }}
                                onChange={(opt) => setImportInsuranceRequired(opt?.value || "No")}
                                isDisabled={!isEditMode}
                              />
                            </div>
                          </div>
                        </>
                      )}

                      {activeGroup.fields.length > 0 && (
                        <>
                          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
                            Manual landed-cost breakup for the whole Purchase Order ({activeGroup.group}). These
                            values are saved together with the rest of the Purchase Order.
                          </div>

                          <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                            <table className="table" style={{ ...styles.erpTable, tableLayout: "auto" }}>
                              <thead>
                                <tr>
                                  {activeGroup.fields.map(f => (
                                    <th key={f.key} style={{ ...styles.erpTh, textAlign: "center" }}>
                                      {f.label}
                                    </th>
                                  ))}
                                  <th style={styles.erpTh}>{activeGroup.group} Subtotal</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {activeGroup.fields.map(f => (
                                    <td key={f.key} style={styles.erpTd}>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm"
                                        style={{ fontSize: "12px", width: "120px", margin: "0 auto" }}
                                        value={entry[f.key] ?? ""}
                                        disabled={!isEditMode}
                                        placeholder="0.00"
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setLandedCosts(prev => ({
                                            ...prev,
                                            [rowKey]: { ...(prev[rowKey] || {}), [f.key]: val }
                                          }));
                                        }}
                                      />
                                    </td>
                                  ))}
                                  <td style={{ ...styles.erpTd, fontWeight: 600, whiteSpace: "nowrap" }}>
                                    ₹ {formatCurrency(groupSubtotal)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  );
                }
              })()}

              {/* ================= REVIEW TAB ================= */}
              {createActiveTab === "review" && (
                <div>
                  <div className="d-flex justify-content-end gap-2 mb-2">
                    <button
                      className="btn btn-dark btn-sm"
                      onClick={handleApprove}
                      disabled={isPOFinalized}
                    >
                      Approve
                    </button>

                    <button
                      className="btn btn-dark btn-sm"
                      onClick={handleReject}
                      disabled={isPOFinalized}
                    >
                      Reject
                    </button>

                    <button
                      className="btn btn-dark btn-sm"
                      onClick={handleQuery}
                      disabled={isPOFinalized}
                    >
                      Query
                    </button>
                  </div>

                  <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                    <table className="table" style={styles.erpTable}>
                      <thead>
                        <tr>
                          <th style={styles.erpTh}>
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                modalData.length > 0 &&
                                modalData.some(row => !["APPROVED", "REJECTED"].includes(String(row.review || "").toUpperCase())) &&
                                modalData.every((row, idx) => {
                                  const s = String(row.review || "").toUpperCase();
                                  return ["APPROVED", "REJECTED"].includes(s) ? true : selectedRows.includes(idx);
                                })
                              }
                            />
                          </th>
                          <th style={styles.erpTh}>Item</th>
                          <th style={styles.erpTh}>
                            {String(poProductType).trim().toLowerCase() === "asset" ? "Asset Code" : "Product Code"}
                          </th>
                          <th style={styles.erpTh}>
                            {String(poProductType).trim().toLowerCase() === "asset" ? "Asset Description" : "Description"}
                          </th>
                          <th style={styles.erpTh}>Unit Cost</th>
                          <th style={styles.erpTh}>UOM</th>
                          <th style={styles.erpTh}>Currency</th>
                          <th style={styles.erpTh}>Qty</th>
                          <th style={styles.erpTh}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalData.map((row, idx) => {
                          const rowStatusUpper = String(row.review || "").toUpperCase();
                          const isDecided = ["APPROVED", "REJECTED"].includes(rowStatusUpper);
                          const isDisabled = isPOFinalized || isDecided;

                          const statusLabel = (() => {
                            if (rowStatusUpper === "PENDING") return "Pending for Approval";
                            if (rowStatusUpper === "APPROVED") return "Approved";
                            if (rowStatusUpper === "REJECTED") return "Rejected";
                            if (rowStatusUpper === "QUERY") return "Query";
                            return "Pending for Approval";
                          })();

                          return (
                            <tr key={idx}>
                              <td style={styles.erpTd}>
                                <input
                                  type="checkbox"
                                  disabled={isDisabled}
                                  checked={selectedRows.includes(idx)}
                                  onChange={(e) => handleCheckboxChange(e, idx)}
                                />
                              </td>
                              <td style={styles.erpTd}>{idx + 1}</td>
                              <td style={styles.erpTd}>{row.product_code}</td>
                              <td style={styles.erpTd}>{row.description}</td>
                              <td style={styles.erpTd}>{row.product_cost}</td>
                              <td style={styles.erpTd}>{row.uom}</td>
                              <td style={styles.erpTd}>{row.currency || ""}</td>
                              <td style={styles.erpTd}>{row.requirement_qty}</td>
                              <td style={styles.erpTd}>
                                {statusLabel}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ================= CONFIRMATION TAB ================= */}
              {createActiveTab === "confirmation" && (() => {
                const overallStatus = (modalData?.[0]?.status || "").toUpperCase();
                const isApproved = overallStatus === "APPROVED" || overallStatus === "PARTIALLY APPROVED";
                return (
                  <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                      {isApproved && (
                        <button className="btn btn-dark btn-sm" onClick={handleSaveConfirmation}>
                          Save Confirmation
                        </button>
                      )}
                    </div>
                    <table className="table" style={styles.erpTable}>
                      <thead>
                        <tr>
                          <th style={styles.erpTh}>Item</th>
                          <th style={styles.erpTh}>Schedule Line</th>
                          <th style={styles.erpTh}>Product</th>
                          <th style={styles.erpTh}>Qty</th>
                          <th style={styles.erpTh}>Delivery Date</th>
                          <th style={styles.erpTh}>Confirm Qty</th>
                          <th style={styles.erpTh}>Confirm Date</th>
                          <th style={styles.erpTh}>Deliver Qty</th>
                          <th style={styles.erpTh}>Deliver Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalData
                          .map((row, originalIdx) => ({ row, originalIdx }))
                          .filter(({ row }) => String(row.review || "").toUpperCase() === "APPROVED")
                          .map(({ row, originalIdx: idx }) => {
                          const rowKey = row.id || `idx_${idx}`;
                          const lines = confirmationLines[rowKey] || [{ schedule_line: "", confirm_qty: "", confirm_date: "", deliver_qty: "", deliver_date: "" }];
                          const totalConfirmedAllLines = lines.reduce((sum, l) => sum + (parseFloat(l.confirm_qty) || 0), 0);
                          const remainingQtyAllLines = (parseFloat(row.requirement_qty) || 0) - totalConfirmedAllLines;
                          const canAddMoreLines = remainingQtyAllLines > 0;
                          return lines.map((line, lineIdx) => (
                            <tr key={`${idx}-${lineIdx}`}>
                              <td style={styles.erpTd}>{lineIdx === 0 ? idx + 1 : ""}</td>
                              {/* Schedule Line - auto-numbered, not manually editable */}
                              <td style={{ ...styles.erpTd, textAlign: "center" }}>
                                {lineIdx + 1}
                              </td>
                              <td style={styles.erpTd}>{lineIdx === 0 ? row.product_code || "" : ""}</td>
                              {/* Qty: original qty shown bold on line 1; subsequent schedule lines show
                                  the remaining unconfirmed balance below, prefixed with "-" */}
                              <td style={styles.erpTd}>
                                {lineIdx === 0 ? (
                                  <span style={{ fontWeight: "bold" }}>{row.requirement_qty || ""}</span>
                                ) : (
                                  (() => {
                                    const originalQty = parseFloat(row.requirement_qty) || 0;
                                    const confirmedSoFar = lines
                                      .slice(0, lineIdx)
                                      .reduce((sum, l) => sum + (parseFloat(l.confirm_qty) || 0), 0);
                                    const balanceQty = originalQty - confirmedSoFar;
                                    return (
                                      <span style={{ color: "#dc2626" }}>
                                        - {balanceQty.toFixed(4)}
                                      </span>
                                    );
                                  })()
                                )}
                              </td>
                              <td style={styles.erpTd}>{lineIdx === 0 ? formatDateDMY(row.delivery_date) || "" : ""}</td>
                              {/* Confirm Qty */}
                              <td style={styles.erpTd}>
                                <input
                                  className="form-control form-control-sm"
                                  style={{ fontSize: "12px", width: "80px" }}
                                  value={line.confirm_qty || ""}
                                  disabled={!isApproved}
                                  onChange={(e) => {
                                    const updated = { ...confirmationLines };
                                    updated[rowKey] = lines.map((l, i) => i === lineIdx ? { ...l, confirm_qty: e.target.value } : l);
                                    setConfirmationLines(updated);
                                  }}
                                />
                              </td>
                              {/* Confirm Date */}
                              <td style={styles.erpTd}>
                                <DateInputDDMMYYYY
                                  className="form-control form-control-sm"
                                  style={{ fontSize: "12px" }}
                                  value={line.confirm_date || ""}
                                  disabled={!isApproved}
                                  onChange={(val) => {
                                    const updated = { ...confirmationLines };
                                    updated[rowKey] = lines.map((l, i) => i === lineIdx ? { ...l, confirm_date: val } : l);
                                    setConfirmationLines(updated);
                                  }}
                                />
                              </td>
                              {/* Deliver Qty */}
                              <td style={styles.erpTd}>
                                <input
                                  className="form-control form-control-sm"
                                  style={{ fontSize: "12px", width: "80px" }}
                                  value={line.deliver_qty || ""}
                                  disabled={!isApproved}
                                  onChange={(e) => {
                                    const updated = { ...confirmationLines };
                                    updated[rowKey] = lines.map((l, i) => i === lineIdx ? { ...l, deliver_qty: e.target.value } : l);
                                    setConfirmationLines(updated);
                                  }}
                                />
                              </td>
                              {/* Deliver Date + + button */}
                              <td style={{ ...styles.erpTd, whiteSpace: "nowrap" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <DateInputDDMMYYYY
                                    className="form-control form-control-sm"
                                    style={{ fontSize: "12px" }}
                                    value={line.deliver_date || ""}
                                    disabled={!isApproved}
                                    onChange={(val) => {
                                      const updated = { ...confirmationLines };
                                      updated[rowKey] = lines.map((l, i) => i === lineIdx ? { ...l, deliver_date: val } : l);
                                      setConfirmationLines(updated);
                                    }}
                                  />
                                  {isApproved && lineIdx === lines.length - 1 && canAddMoreLines && (
                                    <button
                                      className="btn btn-dark btn-sm"
                                      style={{ padding: "0 8px", fontSize: 16, lineHeight: "24px" }}
                                      onClick={() => {
                                        const updated = { ...confirmationLines };
                                        updated[rowKey] = [...lines, { schedule_line: String(lines.length + 1), confirm_qty: "", confirm_date: "", deliver_qty: "", deliver_date: "" }];
                                        setConfirmationLines(updated);
                                      }}
                                    >+</button>
                                  )}
                                  {isApproved && lineIdx > 0 && (
                                    <button
                                      className="btn btn-outline-danger btn-sm"
                                      style={{ fontSize: 12 }}
                                      onClick={() => {
                                        const updated = { ...confirmationLines };
                                        updated[rowKey] = lines
                                          .filter((_, i) => i !== lineIdx)
                                          .map((l, i) => ({ ...l, schedule_line: String(i + 1) }));
                                        setConfirmationLines(updated);
                                      }}
                                    >✕</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              

              {createActiveTab === "docs" && (
                <div style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>

                  {/* ── PO DOCUMENT CARD (RFQ style) ── */}
                  {isFieldVisible("purchase_order_pdf") && modalData?.[0]?.purchase_order_id && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        padding: "12px 16px",
                        background: "#fff",
                      }}>

                        {/* Filename on top */}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "7px",
                          fontWeight: 600,
                          fontSize: 13,
                          color: "#1a1a2e",
                          marginBottom: "10px",
                        }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          {modalData[0].purchase_order_id}_PurchaseOrder.pdf
                        </div>

                        {/* View / Download / Print buttons below filename */}
                        <div style={{ display: "flex", gap: "8px" }}>

                          {/* View — opens popup */}
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() => setShowPODocInModal(true)}
                          >
                            View
                          </button>

                          {/* Download */}
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() => {
                              // Reuses the exact-view download from PODocsView.js
                              // (same page the View/Print buttons already use)
                              // instead of the old DB-rebuilt PDF, so this always
                              // matches what's on screen.
                              window.open(
                                `/po-docs/${modalData[0].purchase_order_id}?autoDownload=1`,
                                "_blank"
                              );
                            }}
                          >
                            Download
                          </button>

                          {/* Print */}
                          <button
                            className="btn btn-outline-dark btn-sm"
                            onClick={() => {
                              // Let /po-docs load its own data and trigger
                              // window.print() itself once poData is ready
                              // (see the autoPrint effect in PODocsView.js).
                              // Calling printWindow.print() from here in
                              // onload used to fire before the async PO
                              // fetch resolved, printing an empty (0-row)
                              // page.
                              window.open(
                                `/po-docs/${modalData[0].purchase_order_id}?autoPrint=1`,
                                "_blank"
                              );
                            }}
                          >
                            Print
                          </button>

                        </div>
                      </div>
                    </div>
                  )}

                  {/* File upload — same styled drop zone as Purchase Request's Docs tab */}
                  {isFieldVisible("uploaded_files") && (
                  <div style={{ marginBottom: 16 }}>
                    <label
                      htmlFor="poDocFileInput"
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center",
                        justifyContent: "center", border: "2px dashed #cbd5e1", borderRadius: 8,
                        padding: "18px 12px", cursor: "pointer",
                        background: "#f8fafc"
                      }}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <span style={{ fontSize: 22 }}>📁</span>
                      <span style={{ fontSize: 13, color: "#475569", fontWeight: 500, marginTop: 4 }}>
                        {docsUploading ? "Uploading…" : "Click to attach documents"}
                      </span>
                      <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                        PDF, DOCX, XLSX (max {maxPoDocSizeKb} KB per file)
                      </span>
                      <input
                        id="poDocFileInput"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleDocUpload}
                        disabled={docsUploading}
                      />
                    </label>
                  </div>
                  )}

                  {/* Uploaded files list — pulled from the server (Google Drive-backed) */}
                  {isFieldVisible("uploaded_files") && (
                  <div>
                    {uploadedDocs.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "20px 0", color: "#94a3b8", fontSize: 13 }}>
                        No documents attached yet.
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={styles.erpTh}>#</th>
                            <th style={styles.erpTh}>File Name</th>
                            <th style={styles.erpTh}>Size</th>
                            <th style={styles.erpTh}>Uploaded At</th>
                            <th style={styles.erpTh}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadedDocs.map((doc, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                              <td style={styles.erpTd}>{idx + 1}</td>
                              <td style={styles.erpTd}>
                                <span style={{ color: "#1e293b", fontWeight: 500 }}>{doc.originalname || doc.filename}</span>
                              </td>
                              <td style={styles.erpTd}>
                                {doc.size
                                  ? doc.size > 1024 * 1024
                                    ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB`
                                    : `${(doc.size / 1024).toFixed(1)} KB`
                                  : '—'}
                              </td>
                              <td style={styles.erpTd}>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : '—'}</td>
                              <td style={styles.erpTd}>
                                <button
                                  type="button"
                                  onClick={() => handleDocDownload(doc)}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    color: '#2563eb', fontWeight: 600, fontSize: 12,
                                    textDecoration: 'none', padding: '3px 10px',
                                    border: '1px solid #bfdbfe', borderRadius: 5,
                                    background: '#eff6ff', cursor: 'pointer'
                                  }}
                                >
                                  ⬇ Download
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  )}

                </div>
              )}

                  {/* ── VIEW POPUP MODAL — iframe loads /po-docs/:poId ── */}
                  {showPODocInModal && modalData?.[0]?.purchase_order_id && (
                    <div style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "rgba(0,0,0,0.55)",
                      zIndex: 99999,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}>
                      <div style={{
                        width: "90%",
                        height: "90%",
                        background: "#fff",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
                      }}>

                        {/* Modal header */}
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 20px",
                          borderBottom: "1px solid #e2e8f0",
                          background: "#f8fafc",
                          flexShrink: 0,
                        }}>
                          <span style={{
                            fontWeight: 600,
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            color: "#1a1a2e",
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            {modalData[0].purchase_order_id}_PurchaseOrder.pdf
                          </span>

                          <div style={{ display: "flex", gap: 8 }}>





                            {/* Close */}
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setShowPODocInModal(false)}
                            >
                              ✕ Close
                            </button>

                          </div>
                        </div>

                        {/* iframe loads /po-docs/:poId */}
                        <iframe
                          id="po-doc-iframe"
                          src={`/po-docs/${modalData[0].purchase_order_id}`}
                          title="PO Document"
                          style={{
                            flex: 1,
                            width: "100%",
                            border: "none",
                            background: "#fff",
                          }}
                        />

                      </div>
                    </div>
                  )}


              {/* ================= CHANGE LOG TAB ================= */}
              {createActiveTab === "change_log" && (
                <div style={styles.tableContainer}>
                  <table className="table" style={styles.erpTable}>
                    <thead>
                      <tr>
                        <th style={styles.erpTh}>Date & Time</th>
                        <th style={styles.erpTh}>User</th>
                        <th style={styles.erpTh}>Action</th>
                        <th style={styles.erpTh}>Field</th>
                        <th style={styles.erpTh}>Old Value</th>
                        <th style={styles.erpTh}>New Value</th>
                        <th style={styles.erpTh}>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changeLogs.length > 0 ? (
                        changeLogs.map((log, idx) => {
                          const dateVal = log.created_at || log.updated_at;
                          return (
                            <tr key={idx}>
                              <td style={styles.erpTd}>
                                {(() => {
                                  const d = new Date(dateVal);
                                  if (isNaN(d.getTime())) return "";
                                  const dd = String(d.getDate()).padStart(2, "0");
                                  const mm = String(d.getMonth() + 1).padStart(2, "0");
                                  const yyyy = d.getFullYear();
                                  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
                                  return `${dd}/${mm}/${yyyy}, ${time}`;
                                })()}
                              </td>
                              <td style={styles.erpTd}>{log.created_by || log.updated_by || "System"}</td>
                              <td style={styles.erpTd}>{log.action_type || "Update"}</td>
                              <td style={styles.erpTd}>
                                {log.field_name
                                  ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                  : '-'}
                              </td>
                              <td style={{ ...styles.erpTd, maxWidth: "150px" }}>
                                {log.old_value || "-"}
                              </td>
                              <td style={{ ...styles.erpTd, maxWidth: "150px" }}>
                                {log.new_value || "-"}
                              </td>
                              <td style={{ ...styles.erpTd, maxWidth: "200px", wordWrap: "break-word" }}>
                                {log.comment || "-"}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          {/* Updated colSpan to 7 to match new column count */}
                          <td colSpan="7" style={{ ...styles.erpTd, textAlign: "center", padding: 20 }}>
                            No changes recorded
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {createActiveTab === "payment_history" && (
                <div style={styles.tableContainer}>

                  <div style={{ position: 'relative', background: '#f8fafc', padding: '16px', paddingTop: '44px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>

                    {(() => {
                      const _totalPaid = advancePayments.reduce((sum, p) => {
                        if (editingPaymentId && p.id === editingPaymentId) return sum;
                        return sum + Number(p.advance || p.amount || 0);
                      }, 0);
                      const _remaining = approvedTotals.grand - _totalPaid;

                      // 🛠️ FIX: approvedTotals.grand is 0 whenever no line item
                      // has been marked APPROVED yet. Previously "_remaining <= 0"
                      // was true in that case too (0 - 0 = 0), so the button showed
                      // "✓ Fully Paid" even though nothing was approved or paid.
                      // We now distinguish "nothing to pay because nothing is
                      // approved" from "fully paid" so the label isn't misleading.
                      const _hasApprovedTotal = approvedTotals.grand > 0;
                      const _isFullyPaid = _hasApprovedTotal && _remaining <= 0;
                      const _disabled = _remaining <= 0; // still disable in both cases

                      let _buttonLabel = '+ Add Payment';
                      if (editingPaymentId) {
                        _buttonLabel = '✓ Update Payment';
                      } else if (_isFullyPaid) {
                        _buttonLabel = '✓ Fully Paid';
                      } else if (!_hasApprovedTotal) {
                        _buttonLabel = 'No Approved Products';
                      }

                      return (
                        <div className="d-flex" style={{ gap: '6px', position: 'absolute', top: '10px', right: '16px' }}>
                          {editingPaymentId && (
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              style={{ height: '32px', whiteSpace: 'nowrap' }}
                              onClick={handleCancelEditPayment}
                              type="button"
                            >
                              Cancel
                            </button>
                          )}
                          <button
                            className="btn btn-dark btn-sm"
                            style={{ height: '32px', whiteSpace: 'nowrap', opacity: _disabled ? 0.5 : 1, cursor: _disabled ? 'not-allowed' : 'pointer' }}
                            onClick={handleAddPaymentRow}
                            disabled={_disabled}
                          >
                            {_buttonLabel}
                          </button>
                        </div>
                      );
                    })()}

                    <div className="row g-3 align-items-start">
                      <div className="col-md">
                        <label style={styles.compactLabel}>Payment Type</label>
                        <select
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentHistoryTabType}
                          onChange={(e) => setPaymentHistoryTabType(e.target.value)}
                        >
                          <option value="Advance">Advance</option>
                          <option value="Paid">Paid</option>
                        </select>
                        {/* 🆕 Shows the total of only APPROVED product lines, matching Sales Order */}
                        <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>
                          Total Price: <strong>₹{formatCurrency(approvedTotals.grand)}</strong>
                        </div>
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Date</label>
                        <DateInputDDMMYYYY
                          style={styles.compactInput}
                          value={paymentHistoryTabDate}
                          onChange={(val) => setPaymentHistoryTabDate(val)}
                        />
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Amount (₹)</label>
                        <input
                          type="number"
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentHistoryTabAmount}
                          onChange={(e) => setPaymentHistoryTabAmount(e.target.value)}
                          placeholder="0.00"
                        />
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Payment Description</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentHistoryTabDescription}
                          onChange={(e) => setPaymentHistoryTabDescription(e.target.value)}
                          placeholder="e.g. NEFT advance against PO"
                        />
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Reference Number</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={paymentHistoryTabReference}
                          onChange={(e) => setPaymentHistoryTabReference(e.target.value)}
                          placeholder="e.g. UTR / Cheque No."
                        />
                      </div>
                    </div>
                  </div>

                  <table className="table mb-0" style={styles.erpTable}>
                    <thead>
                      <tr>
                        <th style={styles.erpTh}>Date</th>
                        <th style={styles.erpTh}>Payment Type</th>
                        <th style={styles.erpTh}>Payment Description</th>
                        <th style={styles.erpTh}>Reference Number</th>
                        <th style={{ ...styles.erpTh, textAlign: 'right' }}>Amount</th>
                        <th style={{ ...styles.erpTh, textAlign: 'right' }}>Balance Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {advancePayments.length > 0 ? (
                        advancePayments.map((row, idx) => (
                          <tr
                            key={row.id || idx}
                            style={editingPaymentId === row.id ? { background: '#fff8e1' } : undefined}
                          >
                            <td style={styles.erpTd}>
                              {formatDateDMY(row.payment_date)}
                            </td>
                            <td style={styles.erpTd}>
                              {row.payment_type || row.paymentType || "Advance"}
                            </td>
                            <td style={styles.erpTd}>
                              {row.payment_description || row.paymentDescription || "-"}
                            </td>
                            <td style={styles.erpTd}>
                              {row.reference_number || row.referenceNumber || "-"}
                            </td>
                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                              {formatCurrency(row.advance || row.amount)}
                            </td>
                            <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 600 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                {formatCurrency(row.pending)}
                                <button
                                  type="button"
                                  onClick={() => handleEditPaymentClick(row)}
                                  title="Edit payment"
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    padding: 0,
                                    lineHeight: 1,
                                    fontSize: '14px',
                                    color: editingPaymentId === row.id ? '#b8860b' : '#6c757d',
                                  }}
                                >
                                  ✎
                                </button>
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted p-3">
                            No payments recorded yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ================= ACKNOWLEDGEMENT TAB ================= */}
              {createActiveTab === "acknowledgement" && (
                <div style={styles.tableContainer}>

                  <div style={{ position: 'relative', background: '#f8fafc', padding: '16px', paddingTop: '44px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>

                    <div className="d-flex" style={{ gap: '6px', position: 'absolute', top: '10px', right: '16px' }}>
                      {editingAckId && (
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          style={{ height: '32px', whiteSpace: 'nowrap' }}
                          onClick={handleCancelEditAcknowledgement}
                          type="button"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        className="btn btn-dark btn-sm"
                        style={{ height: '32px', whiteSpace: 'nowrap' }}
                        onClick={handleSaveAcknowledgement}
                      >
                        {editingAckId ? '✓ Update Acknowledgement' : 'Save Acknowledgement'}
                      </button>
                    </div>

                    <div className="row g-3 align-items-start">
                      <div className="col-md">
                        <label style={styles.compactLabel}>Acknowledgement No</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={ackNoInput}
                          onChange={(e) => setAckNoInput(e.target.value)}
                          placeholder="e.g. ACK-00123"
                        />
                      </div>

                      <div className="col-md">
                        <label style={styles.compactLabel}>Date</label>
                        <DateInputDDMMYYYY
                          style={styles.compactInput}
                          value={ackDateInput}
                          onChange={(val) => setAckDateInput(val)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* ================= FOOTER ================= */}
            <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
              {/* Short Close — for Pending for Approval and Approved, not already short closed */}
              {modalData?.[0]?.purchase_order_id && (() => {
                const s = (modalData?.[0]?.status || "").toUpperCase().trim();
                const ps = (modalData?.[0]?.po_status || "").toUpperCase().trim();
                return (ps !== "SHORT_CLOSED" && (s === "APPROVED" || s === "PENDING FOR APPROVAL")) ? (
                  <button className="btn btn-outline-danger btn-sm" onClick={handleShortClose}>
                    Short Close
                  </button>
                ) : null;
              })()}

              {modalData?.[0]?.purchase_order_id ? (
                (() => {
                  const rawStatus = modalData?.[0]?.status || modalData?.[0]?.po_status || "";
                  const poStatus = rawStatus.toUpperCase().trim();

                  // CREATED = converted from a PR but not yet submitted.
                  // Not reviewable until Submit moves it to
                  // "Pending for Approval" (see submitPurchaseOrder).
                  if (poStatus === "CREATED") {
                    // CREATED PO: fields open directly editable (see
                    // setIsEditMode(poStatus === "CREATED") on load) — no
                    // separate Edit/Cancel step. Submit saves whatever was
                    // just typed/selected (Basic tab, products, etc.) and
                    // then moves the PO to Pending for Approval.
                    return (
                      <button
                        className="btn btn-dark btn-sm"
                        disabled={isSubmittingPO}
                        onClick={async () => {
                          if (isSubmittingPO) return; // guard against double-click double-submit
                          setIsSubmittingPO(true);
                          try {
                            await handleSave(() => handleSubmitPO());
                          } finally {
                            setIsSubmittingPO(false);
                          }
                        }}
                        title={!selectedVendor ? "Assign supplier to save PO" : ""}
                      >
                        {isSubmittingPO ? "Submitting..." : "Submit"}
                      </button>
                    );
                  }

                  // PENDING FOR APPROVAL = submitted, waiting for review
                  if (poStatus === "PENDING FOR APPROVAL") {
                    return isEditMode ? (
                      <>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => setIsEditMode(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={handleSave}
                          title={!selectedVendor ? "Assign supplier to save PO" : ""}
                        >
                          Save
                        </button>
                      </>
                    ) : (
                      canEditPO && (
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() => setIsEditMode(true)}
                        >
                          Edit
                        </button>
                      )
                    );
                  }

                  // APPROVED / REJECTED / others.
                  //
                  // 🆕 If this Import PO has never had actuals saved to
                  // custom_purchase_orders yet, don't make the user click
                  // Edit first — the screen already opened editable (see
                  // setIsEditMode(... || !hasActuals) above), so just show
                  // a single Save button. Once saved, hasCustomData flips
                  // true on the next load and it falls back to the normal
                  // Edit ⇄ Cancel/Save toggle.
                  if (!hasCustomData) {
                    return (
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={handleSave}
                        title={!selectedVendor ? "Assign supplier to save PO" : ""}
                      >
                        Save
                      </button>
                    );
                  }

                  return isEditMode ? (
                    <>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setIsEditMode(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={handleSave}
                        title={!selectedVendor ? "Assign supplier to save PO" : ""}
                      >
                        Save
                      </button>
                    </>
                  ) : (
                    canEditPO && (
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={() => setIsEditMode(true)}
                      >
                        Edit
                      </button>
                    )
                  );
                })()
              ) : (
                /* New (create) PO: always show Save */
                <button
                  className="btn btn-dark btn-sm"
                  onClick={handleSave}
                  title={!selectedVendor ? "Assign supplier to save PO" : ""}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showProductModal && productDetails && (
        <div style={styles.modalBackdrop}>
          <div
            style={{
              ...styles.modal,
              maxWidth: "950px",
              width: "95%",
              maxHeight: "85vh",
              padding: "14px",
              fontSize: "13px"
            }}
          >


            {/* ================= BODY ================= */}
            <div style={{ padding: "12px" }}>

              {/* ===== TOP ROW (LIKE PIC) ===== */}
              <table className="table table-bordered table-sm mb-3">
                <thead style={{ background: "#f8fafc", fontWeight: 600 }}>
                  <tr>
                    <th>{String(poProductType).trim().toLowerCase() === "asset" ? "Asset Code" : "Product Code"}</th>
                    <th>HSN Code</th>
                    <th>Quantity</th>
                    <th>Unit Cost</th>
                    <th>Discount (%)</th>
                    <th>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{productDetails.product_code}</td>
                    <td>{productDetails.hsnCode}</td>
                    <td>{productDetails.requirement_qty}</td>
                    <td>₹ {formatCurrency(productDetails.product_cost)}</td>
                    <td>{productDetails.discount}</td>
                    <td style={{ fontWeight: 600 }}>
                      ₹ {formatCurrency(productDetails.totalCost)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* ===== NET PRICE LINE ===== */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 10px",
                  borderTop: "1px solid #999",
                  borderBottom: "1px solid #999",
                  marginBottom: "10px",
                  fontWeight: 600
                }}
              >
                <span>Net Price :</span>
                <span>₹ {formatCurrency(productDetails.netPriceAfterDiscount)}</span>
              </div>

              {/* ===== GST BREAKUP (dynamic — one row per actual tax type) ===== */}
              <table
                className="table table-sm"
                style={{ width: "50%", marginLeft: "auto" }}
              >
                <tbody>
                  {(productDetails.taxes && productDetails.taxes.length
                    ? productDetails.taxes
                    : [{ type: "CGST", percent: 0, value: 0 }]
                  ).map((t, i) => (
                    <tr key={i}>
                      <td>{t.type} @ {t.percent}%</td>
                      <td style={{ textAlign: "right" }}>
                        ₹ {formatCurrency(t.value)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid #000", fontWeight: 600 }}>
                    <td>Total GST</td>
                    <td style={{ textAlign: "right" }}>
                      ₹ {formatCurrency(productDetails.totalGST)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* ===== GRAND TOTAL ===== */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "12px",
                  marginTop: "8px",
                  background: "#f1f5f9",
                  fontWeight: 700,
                  fontSize: "15px",
                  borderRadius: "4px"
                }}
              >
                <span>Grand Total :</span>
                <span>₹ {formatCurrency(productDetails.finalPriceAfterGST)}</span>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowProductModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showPaymentModal && (
        <div style={styles.modalBackdrop}>
          <div
            style={{
              ...styles.modal,
              maxWidth: "700px"
            }}
          >

            <div style={styles.modalHeader}>
              <strong>Payment Terms</strong>

              <button
                style={styles.iconBtn}
                onClick={() => setShowPaymentModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>

              <textarea
                className="form-control"
                rows={12}
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
              />

            </div>

            <div style={styles.modalFooter}>

              <button
                className="btn btn-secondary"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-dark"
                onClick={() => setShowPaymentModal(false)}
              >
                Save
              </button>

            </div>

          </div>
        </div>
      )}
      {/* ================= CHARACTERISTICS MODAL ================= */}
      <CharacteristicsModal
        modalVisible={modalVisible}
        selectedCharacteristics={selectedCharacteristics}
        handleClose={handleSaveCharModal}
        isEditing={isEditMode}
        handleChange={onModalCharChange}
      />

      {/* ================= COMMENT MODAL ================= */}
      {commentModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: "500px" }}>
            <div style={styles.modalHeader}>
              <strong>{actionType} Purchase Order</strong>

              <button
                style={styles.iconBtn}
                onClick={() => setCommentModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>

              {actionType === "APPROVED" && (
                <div className="row g-2 mb-3">



                </div>
              )}

              <label style={styles.compactLabel}>
                Comment / Remarks
              </label>

              <textarea
                className="form-control"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter your comment here"
              />

            </div>

            <div style={styles.modalFooter}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setCommentModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-dark btn-sm"
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />


    </Layout >
  );
};

const styles = {
  compactLabel: {
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "2px",
    color: "#334155"
  },
  compactInput: {
    fontSize: "14px",
    height: "32px",
    padding: "4px 8px"
  },
  compactTabBtn: {
    fontSize: "14px",
    padding: "4px 10px"
  }
  ,
  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },

  erpTable: {
    width: "100%",
    minWidth: "600px",
    tableLayout: "fixed",
    borderCollapse: "collapse"
  },

  erpTh: {
    background: "#f8fafc",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b",
    whiteSpace: "nowrap"
  },

  erpTd: {
    fontSize: 13,
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155",
    verticalAlign: "middle"
  }
  ,
  filterCard: {
    background: '#ffffff',
    borderRadius: 8,
    padding: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    marginBottom: 18
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
    color: '#334155'
  },
  filterSelect: {
    height: 36,
    fontSize: 13
  }
  ,
  pageTitleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: '10px',
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: 2
  },
  pageSubtitle: {
    fontSize: 13,
    color: '#64748b'
  },
  primaryBtn: { paddingLeft: 14, paddingRight: 14 },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '20px 12px',
    overflowY: 'auto',
  },
  modal: {
    background: '#fff',
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    padding: '15px 20px',
    borderBottom: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
  },
  modalBody: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  modalFooter: {
    padding: '15px 20px',
    borderTop: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  tabs: {
    display: 'flex',
    gap: 8,
    margin: '14px 0',
  },
};

export default Custom;